/* <speedi-sleeve-selector> (sss1): UI only. Every selection rule lives in speedi-sleeve-core.js. Contract: docs/SPEC.md 2-5. */

const VERSION = 'sss1';
const SCHEMA_VERSION = 1;
const MAX_READINGS = 6;
const START_READINGS = 3;
const LISTINGS_TTL_MS = 10 * 60 * 1000;
const LISTINGS_TIMEOUT_MS = 15000;
const MAX_LISTING_PAGES = 20;
const ROUND_TRIP_TTL_MS = 2 * 60 * 60 * 1000;
const ANNOUNCE_DELAY_MS = 500;
const CORE_EXPORTS = [
  'toMm', 'fromMm', 'formatLength', 'parseReading', 'summarizeReadings', 'convertReadingText',
  'detectUnitMistake', 'edgeBand', 'matchSleeves', 'spreadWarning', 'checkGeometry', 'normalizeQuery',
  'lookupPart', 'indexListings', 'pickListing', 'decideAction', 'sortParts', 'encodeShare', 'decodeShare',
  'buildQuoteSummary',
];
const LISTING_DECIDED = new Set(['available', 'sold_out', 'unlisted']);
const QUOTE_KEYS = ['Readings', 'Average', 'Unit', 'Candidates', 'Worn band', 'Reason', 'Data edition', 'Share code'];
const KEEP_FIELDS = '[data-sss-keep]';
const GEO_KEYS = ['tn', 'tf', 'c', 'o'];
const BLOCK_KEYS = ['k', 'e', 'l'];
const DUP_TAG = 'Speedi-sleeve';
const PN_RE = /^\d{5}$/;
/** Stockmart's button classes plus ours (DESIGN-SEO 1): the theme styles them; our CSS is the fallback. */
const BTN = {
  primary: 'button button--primary sss-btn sss-btn--primary',
  secondary: 'button button--secondary sss-btn sss-btn--secondary',
  link: 'sss-btn sss-btn--link',
};

/* ---------- Copy (plain language; symbols only on the printed spec sheet) ---------- */

const TYPE_LABEL = {
  STD: 'Standard (stainless) — preferred',
  GOLD: 'Gold (hard coating, abrasive duty)',
  UNCONFIRMED: 'Type to be confirmed with SKF',
};
const TYPE_SHORT = { STD: 'Standard', GOLD: 'Gold', UNCONFIRMED: 'Type to confirm' };
const STATE_COPY = {
  fits: { cls: 'fits', icon: '✓', text: 'Fits' },
  near_edge: { cls: 'near', icon: '!', text: 'Near the edge of the range — re-measure with a micrometer' },
  borderline: { cls: 'border', icon: '?', text: "At the edge of SKF's published range — confirm with us" },
  just_outside: { cls: 'out', icon: '×', text: 'Does not fit — re-measure' },
  spread: { cls: 'near', icon: '!', text: 'Your readings disagree — re-measure' },
};
const BORDERLINE_DISAGREE = { cls: 'border', icon: '?', text: "SKF's catalogues differ at this size — confirm with us" };
const QUOTE_WHY = {
  sold_out: ['Out of stock — ask for lead time', 'Ask for lead time'],
  unlisted: ['Not stocked online — we can order it', 'Request a quote'],
  borderline: ['Confirm the fit with us before ordering', 'Ask us to confirm'],
  single_source: ['Listed in only one SKF edition — check availability', 'Check availability'],
  geometry: ["Won't fit your worn band position", 'Ask about options'],
  excluded: ['Needs confirming with SKF', 'Ask us to confirm'],
};
const REASON_LABEL = {
  unlisted: 'not stocked online', sold_out: 'out of stock', borderline: 'borderline reading',
  single_source: 'check availability', gap: 'no standard size', below_min: 'below smallest size',
  no_standard: 'no standard size', ldslv: 'LDSLV heavy-duty sleeve', above_ldslv: 'over 45 in',
  gold_on_request: 'Gold on request', blocked: 'cannot fit a Speedi-Sleeve', geometry: 'worn band position',
  lookup_unknown: 'unknown part number', competitor_unknown: 'other-brand part number',
  excluded: 'part to confirm', confirm_fit: 'confirm fit', general: 'general',
};
const BLOCK_COPY = {
  k: 'A keyway, cross hole, spline or thread under the sleeve would distort it, and the seal would leak.',
  e: 'The sleeve has to slide on over a free shaft end, with the pulley, coupling or gear removed.',
  l: "The sleeve can't pass a larger diameter between the shaft end and the worn band.",
};
const GEO_REASON = {
  band_wider_than_part: 'the worn band is wider than this sleeve covers',
  chamfer_too_long: "the chamfer or radius is too long, so the sleeve's outer edge wouldn't sit on full diameter",
  obstruction_too_close: 'the part that stays on the shaft is too close: the flange would hit it before the sleeve covers the band',
  tn_after_tf: 'the far edge must be further from the shaft end than the near edge',
};
const READING_ERRORS = {
  empty: 'Enter your first reading, for example 1.0005',
  invalid: 'Use numbers only, for example 1.0005 (no fractions or units)',
  not_positive: 'Enter a reading greater than 0',
};
const GEO_ERRORS = {
  invalid: 'Use numbers only, for example 0.350',
  not_positive: 'Enter a distance of 0 or more',
  o_positive: 'Enter a distance greater than 0, or leave it empty if nothing stays on the shaft',
  order: 'The far edge must be further from the shaft end than the near edge',
};
const BRAND_LABEL = { SKF: 'SKF', CR: 'CR', CHR: 'CHR', FMS: 'FMS', KWK: 'KWK', NATIONAL: 'National', TIMKEN: 'Timken' };
const FIT_SHORT = { fits: 'Fits', near_edge: 'Near edge', borderline: 'Borderline', just_outside: 'No' };
const LEGEND = [
  ['d1', 'Fits shafts (min–max)'], ['b1', 'Covers (width on shaft)'], ['b', 'Total width incl. removable flange'],
  ['D', 'Flange OD'], ['B', 'Max install depth with included tool'], ['STD', 'Standard (stainless)'],
  ['Gold', 'Gold (hard coating, abrasive duty)'],
];

/* ---------- Small helpers ---------- */

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
const otherUnit = (unit) => (unit === 'in' ? 'mm' : 'in');
const fixed = (n, unit) => (Number.isFinite(Number(n)) ? Number(n).toFixed(unit === 'in' ? 3 : 2) : '');
const plural = (n, one, many) => (n === 1 ? one : many);
/** Typed text + its unit: the source of truth across unit switches. */
const src = (text = '', unit = 'in') => ({ text: String(text ?? '').slice(0, 40), unit: unit === 'mm' ? 'mm' : 'in' });
const filled = (s) => String(s?.text ?? '').trim() !== '';

/** Config JSON (SPEC 3) with defaults; null when unusable. */
export function readConfig(raw) {
  if (!raw || typeof raw !== 'object' || !raw.coreUrl || !raw.dataUrl) return null;
  return {
    ...raw,
    routes: {
      cart_add_url: '/cart/add', cart_url: '/cart', root_url: '/',
      predictive_search_url: '/search/suggest', search_url: '/search', ...(raw.routes || {}),
    },
    dupTag: (typeof raw.dupTag === 'string' && raw.dupTag.trim()) || DUP_TAG,
    defaultUnit: raw.defaultUnit === 'mm' ? 'mm' : 'in',
    showPrices: raw.showPrices !== false,
    allowCartBorderline: raw.allowCartBorderline === true,
    quote: { formId: '', posted: false, hasErrors: false, ...(raw.quote || {}) },
  };
}

/** Degraded mode: a duplicate-only search result means the original was missed (e.g. hidden sold out): quote. */
export function pickDegraded(core, pn, items, dupTag) {
  const tag = (typeof dupTag === 'string' && dupTag.trim()) || DUP_TAG;
  const result = core.pickListing(pn, core.indexListings(items), tag);
  return result.listing && Array.isArray(result.listing.t) && result.listing.t.includes(tag)
    ? { status: 'unlisted', listing: null, duplicateAvailable: true, dupOnly: true }
    : result;
}

function store(area) {
  return {
    get(key) { try { return window[area].getItem(key); } catch { return null; } },
    set(key, value) { try { window[area].setItem(key, value); return true; } catch { return false; } },
    remove(key) { try { window[area].removeItem(key); } catch { /* storage blocked */ } },
  };
}
const local = store('localStorage');
const session = store('sessionStorage');

function readJson(text) {
  if (!text) return null;
  try { return JSON.parse(text); } catch { return null; }
}

function safePath(url, fallback) {
  const s = String(url || '');
  return /^\/(?!\/)/.test(s) ? s : fallback;
}

function cleanQuoteValue(value) {
  return String(value ?? '')
    .replace(/https?:\/\/\S+/gi, '')
    .replace(/[[\]<>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 1000);
}

function extractListings(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const el = doc.querySelector('script[data-sss-listings]');
  return el ? readJson(el.textContent) : null;
}

function reducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/* ---------- The element ---------- */

// Loads in Node too (tests drive the view methods).
const Base = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class SpeediSleeveSelector extends Base {
  #boot = 'idle';
  #gen = 0;
  #abort = null;
  #globalOff = [];
  #announceTimer = 0;
  #pendingAnnounce = null;
  #lastAnnounced = '';
  #lastSearchKey = '';
  #lastLookupKey = '';
  #html = new WeakMap();
  #dismissedMistake = '';
  #dismissedReadings = new Set();
  #warned = false;
  #howtoClosedWide = false;

  constructor() {
    super();
    this.state = {
      mode: null,
      unit: 'in',
      readings: Array.from({ length: START_READINGS }, () => src()),
      geo: { tn: src(), tf: src(), c: src(), o: src() },
      answers: { k: false, e: false, l: false, g: null, a: false, f: false },
      lookupText: '',
    };
    this.core = null;
    this.data = null;
    this.listings = { state: 'idle', index: null };
    this.degraded = new Map();
    this.cartBusy = new Set();
    this.cartMsg = new Map();
    this.qty = new Map();
    this.openDetails = new Set();
    this.touched = new Set();
    this.submitted = false;
    this.lookupSubmitted = false;
    this.quoteSubmitting = false;
    this.toolsMsg = null;
    this.quoteCtx = { reason: 'general', pns: [] };
    this.lastVm = null;
    this.lastLookup = null;
  }

  connectedCallback() {
    this.#abort = new AbortController();
    this.#bindGlobal();
    if (this.#boot === 'idle') this.#start();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    for (const off of this.#globalOff) off();
    this.#globalOff = [];
    clearTimeout(this.#announceTimer);
    document.documentElement.classList.remove('sss-printing');
  }

  /* ----- Boot ----- */

  async #start() {
    const gen = ++this.#gen;
    this.#boot = 'loading';
    this.config = readConfig(readJson(this.querySelector('script[data-sss-config]')?.textContent));
    if (!this.config) {
      this.#boot = 'error';
      this.#renderLoadError();
      return;
    }
    this.design = Boolean(this.config.designMode || window.Shopify?.designMode);
    this.idBase = `sss-${String(this.config.sectionId || 'selector').replace(/[^\w-]/g, '')}`;
    const savedUnit = local.get('sss:unit');
    this.state.unit = savedUnit === 'mm' || savedUnit === 'in' ? savedUnit : this.config.defaultUnit;
    if (!this.#listenersBound) this.#bindLocal();
    this.#wireQuoteForm();

    const signal = this.#abort.signal;
    // Settled now (no unhandled rejection); applied once the core is in.
    const listingsPromise = this.design ? null
      : this.#loadListings(signal).then((items) => ({ items }), (error) => ({ error }));
    try {
      const [core, data] = await Promise.all([
        import(this.config.coreUrl),
        this.#fetchJson(this.config.dataUrl, signal),
      ]);
      const missing = CORE_EXPORTS.filter((name) => typeof core[name] !== 'function');
      if (missing.length) throw new Error(`core is missing ${missing.join(', ')}`);
      const expected = core.SCHEMA_VERSION ?? SCHEMA_VERSION;
      if (!data || data.schema_version !== expected || !Array.isArray(data.parts)) {
        throw new Error(`data schema_version ${data?.schema_version} does not match ${expected}`);
      }
      if (signal.aborted) throw new Error('aborted');
      this.core = core;
      this.data = data;
      this.partsByPn = new Map(data.parts.map((p) => [p.pn, p]));
    } catch (error) {
      if (gen !== this.#gen) return; // superseded by a retry
      if (signal.aborted) {
        // Moved in the DOM while loading: connectedCallback saw 'loading', so restart here.
        this.#boot = 'idle';
        if (this.isConnected) this.#start();
        return;
      }
      console.warn(`[speedi-sleeve-selector ${VERSION}] could not load`, error);
      this.#boot = 'error';
      this.#renderLoadError();
      return;
    }
    if (gen !== this.#gen) return;

    this.#renderApp();
    this.#boot = 'ready';
    const edition = this.querySelector('[data-sss-edition]');
    if (edition && this.data.edition_short) edition.textContent = ` (${this.data.edition_short})`;

    if (listingsPromise) {
      this.listings = { state: 'loading', index: null };
      listingsPromise.then((r) => (r.error ? this.#listingsFailed(r.error) : this.#listingsReady(r.items)));
    }

    if (this.design) this.#applySample();
    else if (!this.#restoreRoundTrip()) this.#applyHash();
    // An entry tab tapped while loading opens once we are ready.
    if (this.pendingMode && !this.state.mode) this.#setMode(this.pendingMode, { focus: true });
    else if (!this.state.mode) this.#setMode('measure'); // the measure panel is open in the server-rendered shell
  }

  async #fetchJson(url, signal) {
    const res = await fetch(url, { signal, credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status} for data`);
    return res.json();
  }

  #retry() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.#status('<p class="sss-boot" role="status">Loading the size finder…</p>', false);
    this.#start();
  }

  /** Boot and load-error messages; the server-rendered shell around them stays in place. */
  #status(html, failed) {
    const box = this.querySelector('[data-sss-status]');
    if (box) box.innerHTML = html;
    for (const el of this.querySelectorAll('.sss-entry, .sss-entry__label')) el.hidden = failed;
  }

  /* ----- Listings (price and stock from the store) ----- */

  async #loadListings(signal) {
    const key = `sss:listings:${this.config.cacheKey || ''}`;
    const cached = readJson(session.get(key));
    if (cached && cached.url === this.config.listingsUrl && Array.isArray(cached.items)
      && Date.now() - cached.t < LISTINGS_TTL_MS) return cached.items;

    const ac = new AbortController();
    const onAbort = () => ac.abort();
    signal.addEventListener('abort', onAbort, { once: true });
    const timer = setTimeout(() => ac.abort(), LISTINGS_TIMEOUT_MS);
    try {
      const first = await this.#fetchListingsPage(1, ac.signal);
      const pages = Math.min(Math.max(Number(first.pages) || 1, 1), MAX_LISTING_PAGES);
      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, i) => this.#fetchListingsPage(i + 2, ac.signal)),
      );
      const items = [first, ...rest].flatMap((page) => page.items)
        .filter((item) => item && typeof item === 'object' && typeof item.s === 'string' && item.id != null);
      session.set(key, JSON.stringify({ t: Date.now(), url: this.config.listingsUrl, items }));
      return items;
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    }
  }

  async #fetchListingsPage(page, signal) {
    const url = new URL(this.config.listingsUrl, window.location.href);
    url.searchParams.set('page', String(page));
    const res = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`HTTP ${res.status} for listings page ${page}`);
    const json = extractListings(await res.text());
    if (!json || json.v !== 1 || !Array.isArray(json.items) || json.error) {
      throw new Error(`listings page ${page}: ${json?.error || 'no data-sss-listings JSON'}`);
    }
    return json;
  }

  #listingsReady(items) {
    this.listings = { state: 'ready', index: this.core.indexListings(items) };
    this.#refresh();
  }

  #listingsFailed(error) {
    if (this.#abort?.signal.aborted) return;
    this.listings = { state: 'failed', index: null };
    if (!this.#warned) {
      this.#warned = true;
      console.warn(`[speedi-sleeve-selector ${VERSION}] listings unavailable; checking parts one at a time`, error);
    }
    this.#refresh();
  }

  /** {status, listing?, duplicateAvailable?, failed?} */
  #listingFor(pn) {
    if (this.design) return { status: 'design', listing: null };
    const checked = this.degraded.get(pn);
    if (checked) return checked;
    if (this.listings.state === 'ready') {
      return this.core.pickListing(pn, this.listings.index, this.config.dupTag);
    }
    if (this.listings.state === 'failed') return { status: 'degraded', listing: null };
    return { status: 'pending', listing: null };
  }

  /** Degraded mode: one part via predictive search, then /products/{handle}.js. */
  async #checkListing(pn) {
    if (this.degraded.has(pn)) return;
    this.degraded.set(pn, { status: 'checking', listing: null });
    this.#refresh();
    let result;
    try {
      const root = this.config.routes.root_url.endsWith('/') ? this.config.routes.root_url : `${this.config.routes.root_url}/`;
      const url = new URL(`${this.config.routes.predictive_search_url}.json`, window.location.href);
      url.searchParams.set('q', pn);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][fields]', 'variants.sku');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      const res = await fetch(url, { signal: this.#abort.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status} for predictive search`);
      const products = (await res.json())?.resources?.results?.products || [];
      const handles = products.map((p) => p.handle).filter(Boolean);
      const likely = handles.filter((h) => h.includes(pn.toLowerCase()));
      const items = [];
      for (const handle of (likely.length ? likely : handles).slice(0, 4)) {
        const pr = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { signal: this.#abort.signal, credentials: 'same-origin' });
        if (!pr.ok) continue;
        const product = await pr.json();
        for (const v of product.variants || []) {
          items.push({
            s: String(v.sku || '').trim(), id: v.id, h: product.handle,
            u: safePath(product.url, `${root}products/${product.handle}`),
            p: v.price, pf: this.#money(v.price), c: v.compare_at_price ?? null,
            cf: v.compare_at_price > v.price ? this.#money(v.compare_at_price) : null,
            a: Boolean(v.available), t: Array.isArray(product.tags) ? product.tags : [],
            q: [v.quantity_rule?.min ?? 1, v.quantity_rule?.increment ?? 1, v.quantity_rule?.max ?? null],
          });
        }
      }
      result = pickDegraded(this.core, pn, items, this.config.dupTag);
    } catch (error) {
      if (this.#abort.signal.aborted) return;
      result = { status: 'unlisted', listing: null, duplicateAvailable: false, failed: true };
    }
    this.degraded.set(pn, result);
    this.#refresh();
  }

  #money(cents) {
    const amount = Number(cents) / 100;
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency', currency: this.config.currency || 'USD',
      }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }

  /* ----- Event wiring ----- */

  #bindLocal() {
    this.#listenersBound = true;
    this.addEventListener('click', (e) => this.#onClick(e));
    this.addEventListener('input', (e) => this.#onInput(e));
    this.addEventListener('change', (e) => this.#onChange(e));
    this.addEventListener('submit', (e) => this.#onSubmit(e));
    this.addEventListener('keydown', (e) => this.#onKeydown(e));
    this.addEventListener('focusout', (e) => this.#onFocusOut(e));
    this.addEventListener('toggle', (e) => this.#onToggle(e), true);
  }

  #listenersBound = false;

  #bindGlobal() {
    const on = (target, type, fn) => {
      target.addEventListener(type, fn);
      this.#globalOff.push(() => target.removeEventListener(type, fn));
    };
    on(window, 'hashchange', () => { if (this.#boot === 'ready' && !this.design) this.#applyHash(); });
    on(window, 'afterprint', () => document.documentElement.classList.remove('sss-printing'));
    on(window, 'pageshow', (e) => { if (e.persisted) this.#submitDone(); }); // bfcache
    // The "How to measure" card starts closed below 990px (inline script in the section). Reopen it when the layout
    // widens to the sticky desktop aside (a tablet turned to landscape), unless the visitor closed it there.
    const wide = window.matchMedia?.('(min-width: 990px)');
    const howto = this.querySelector('.sss-howto');
    if (wide && howto) {
      on(howto, 'toggle', () => { if (wide.matches) this.#howtoClosedWide = !howto.open; });
      on(wide, 'change', () => { if (wide.matches && !this.#howtoClosedWide) howto.open = true; });
    }
    // Links elsewhere on the page (the size chart): "Quote" opens our form for that part; a part number opens lookup.
    // Before boot (or after a load error) the link keeps its plain #finder jump.
    on(document, 'click', (e) => {
      const a = e.target.closest?.('[data-sss-quote-pn], [data-sss-lookup-pn]');
      if (!a || e.defaultPrevented || this.#boot !== 'ready') return;
      const pn = String(a.dataset.sssQuotePn ?? a.dataset.sssLookupPn ?? '').trim();
      if (a.dataset.sssQuotePn != null) {
        if (!PN_RE.test(pn)) return;
        e.preventDefault();
        this.#openQuote('unlisted', [pn]);
      } else if (pn) {
        e.preventDefault();
        this.#lookupPn(pn.slice(0, 20));
      }
    });
  }

  #onClick(event) {
    const link = event.target.closest('.sss-errors a[href^="#"], .sss-prompt a[href^="#"]');
    if (link) {
      const target = document.getElementById(decodeURIComponent(link.getAttribute('href').slice(1)));
      if (target) { event.preventDefault(); target.focus(); }
      return;
    }
    const btn = event.target.closest('[data-action]');
    if (!btn || !this.contains(btn)) return;
    const { action, pn, reason, mode } = btn.dataset;
    const index = Number(btn.dataset.index);
    switch (action) {
      case 'mode': this.#setMode(mode, { focus: true }); break;
      case 'add-reading': this.#addReading(); break;
      case 'mistake-accept': this.#acceptMistake(); break;
      case 'mistake-dismiss': this.#dismissMistake(); break;
      case 'reading-accept': this.#readingMistake(index, true); break;
      case 'reading-dismiss': this.#readingMistake(index, false); break;
      case 'answers': this.#showAnswers(); break;
      case 'add': this.#addToCart(btn, pn); break;
      case 'quote': this.#openQuote(reason || 'general', (btn.dataset.pns || '').split(',').filter(Boolean)); break;
      case 'check-price': this.#checkListing(pn); break;
      case 'lookup-pn': this.#lookupPn(pn); break;
      case 'measure': this.#setMode('measure', { focus: true }); break;
      case 'share': this.#share(); break;
      case 'print': this.#print(); break;
      case 'retry': this.#retry(); break;
      case 'quote-back': this.#focusAfterQuote(); break;
      default: break;
    }
  }

  #onInput(event) {
    const t = event.target;
    if (this.#boot !== 'ready') return;
    if (t.matches('[data-sss-reading]')) {
      this.state.readings[Number(t.dataset.sssReading)] = src(t.value, this.state.unit);
      this.toolsMsg = null;
      this.#update();
    } else if (t.matches('[data-sss-geo]')) {
      this.state.geo[t.dataset.sssGeo] = src(t.value, this.state.unit);
      this.#update();
    } else if (t.matches('[data-sss-lookup]')) {
      this.state.lookupText = t.value;
      this.lookupSubmitted = false;
      this.#updateLookup();
    } else if (t.matches('[data-sss-qty]')) {
      this.qty.set(t.dataset.sssQty, t.value);
    }
  }

  #onChange(event) {
    const t = event.target;
    if (this.#boot !== 'ready') return;
    if (t.matches('[data-sss-unit]') && t.checked) {
      this.#setUnit(t.value);
      this.#update();
      if (this.state.mode === 'lookup') this.#updateLookup();
    } else if (t.matches('[data-sss-answer]')) {
      const key = t.dataset.sssAnswer;
      this.state.answers[key] = t.type === 'radio' ? t.value : t.checked;
      this.#update();
    }
  }

  #onSubmit(event) {
    const form = event.target;
    if (form.matches('[data-sss-measure-form]')) {
      event.preventDefault();
      this.submitted = true;
      this.state.readings.forEach((_, i) => this.touched.add(`r${i}`));
      this.#update({ announceNow: true });
      const errors = this.#readingErrors(this.lastVm);
      this.#swap(this.refs.errors, errors.length ? this.#errorSummaryHtml('Check your readings', errors, 3) : '');
      if (errors.length) this.refs.errors.querySelector('.sss-errors')?.focus();
      else if (this.lastVm?.hold) this.refs.prompt.querySelector('button')?.focus(); // unit question first
      else this.#focusResults();
    } else if (form.matches('[data-sss-lookup-form]')) {
      event.preventDefault();
      this.lookupSubmitted = true;
      this.#updateLookup({ announceNow: true });
      const heading = this.refs.lookupResult.querySelector('[data-sss-focus-heading]');
      if (heading) heading.focus();
      else if (this.refs.lookupInput.getAttribute('aria-invalid') === 'true') this.refs.lookupInput.focus();
    } else if (form === this.quoteForm) {
      this.#onQuoteSubmit(event);
    }
  }

  #onKeydown(event) {
    if (event.key !== 'Enter' || event.isComposing) return;
    const t = event.target;
    let next = null;
    if (t.matches('[data-sss-reading]')) {
      next = this.querySelector(`[data-sss-reading="${Number(t.dataset.sssReading) + 1}"]`);
    } else if (t.matches('[data-sss-geo]')) {
      const all = [...this.querySelectorAll('[data-sss-geo]')];
      next = all[all.indexOf(t) + 1] || null;
      if (!next) { event.preventDefault(); t.blur(); this.#flushAnnounce(); return; }
    }
    if (next) { event.preventDefault(); next.focus(); }
  }

  #onFocusOut(event) {
    const t = event.target;
    if (t.matches('[data-sss-reading]')) this.touched.add(`r${t.dataset.sssReading}`);
    else if (t.matches('[data-sss-geo]')) this.touched.add(t.dataset.sssGeo);
    else return;
    if (this.#boot !== 'ready') return;
    this.#renderFieldErrors(this.lastVm);
    this.#flushAnnounce();
  }

  #onToggle(event) {
    const el = event.target;
    if (!(el instanceof HTMLDetailsElement) || !el.dataset.sssDetails) return;
    if (el.open) this.openDetails.add(el.dataset.sssDetails);
    else this.openDetails.delete(el.dataset.sssDetails);
  }

  /* ----- Skeleton ----- */

  #slotHtml(name) {
    const tpl = this.querySelector(`template[data-sss-slot="${name}"]`);
    const html = tpl ? tpl.innerHTML.trim() : '';
    return html.replace(/<[^>]*>/g, '').trim() ? html : '';
  }

  /**
   * Adopt the server-rendered shell (section Liquid): entry tabs, both forms, the result regions, the live region.
   * Only the static check panel is copied in, from <template data-sss-tpl="check">.
   */
  #renderApp() {
    const q = (sel) => this.querySelector(sel);
    const slot = q('[data-sss-check-slot]');
    if (slot) slot.outerHTML = this.#checkPanelHtml();
    this.#status('', false);
    this.refs = {
      app: q('[data-sss-app]'),
      errors: q('[data-sss-errors]'), readings: q('[data-sss-readings]'), addReading: q('[data-action="add-reading"]'),
      check: q('[data-sss-check]'), checkOut: q('[data-sss-check-out]'),
      prompt: q('[data-sss-prompt]'), results: q('[data-sss-results]'), checklist: q('[data-sss-checklist]'),
      lookupInput: q('[data-sss-lookup]'), lookupResult: q('[data-sss-lookup-result]'),
      live: q('[data-sss-live]'), print: q('[data-sss-print]'),
    };
    // Keep anything typed into the server-rendered reading fields before the JS booted.
    const typed = [...this.querySelectorAll('[data-sss-reading]')].map((i) => i.value.trim());
    if (typed.some(Boolean) && !this.state.readings.some((r) => r.text)) this.state.readings = typed.map((t) => src(t, this.state.unit));
    this.#syncInputs();
  }

  #readingFieldHtml(i) {
    const id = `${this.idBase}-r${i}`;
    const req = i === 0 ? ' <span class="sss-field__req">(required)</span>' : '';
    return `
      <div class="sss-field sss-field--reading">
        <label class="sss-field__label" for="${id}">Reading ${i + 1}${req}</label>
        <div class="sss-field__control">
          <input class="sss-field__input" id="${id}" type="text" inputmode="decimal" enterkeyhint="next"
            autocomplete="off" autocorrect="off" spellcheck="false" data-sss-reading="${i}"
            aria-describedby="${this.idBase}-readings-hint ${id}-error" value="${esc(this.#shown(this.state.readings[i]))}">
          <span class="sss-field__suffix" aria-hidden="true" data-sss-suffix>${this.state.unit}</span>
        </div>
        <p class="sss-field__error" id="${id}-error" hidden></p>
      </div>`;
  }

  /** Static panel from the section's <template data-sss-tpl="check">; SSSID = our id prefix. */
  #checkPanelHtml() {
    const tpl = this.querySelector('template[data-sss-tpl="check"]');
    return tpl ? tpl.innerHTML.replaceAll('SSSID', this.idBase) : '';
  }

  /** Push this.state into the inputs. */
  #syncInputs() {
    const r = this.refs;
    if (!r) return;
    const n = Math.min(Math.max(this.state.readings.length, START_READINGS), MAX_READINGS);
    while (this.state.readings.length < n) this.state.readings.push(src());
    r.readings.innerHTML = this.state.readings.map((_, i) => this.#readingFieldHtml(i)).join('');
    r.addReading.hidden = this.state.readings.length >= MAX_READINGS;
    for (const input of this.querySelectorAll('[data-sss-unit]')) input.checked = input.value === this.state.unit;
    for (const el of this.querySelectorAll('[data-sss-suffix]')) el.textContent = this.state.unit;
    for (const input of this.querySelectorAll('[data-sss-geo]')) input.value = this.#shown(this.state.geo[input.dataset.sssGeo]);
    for (const input of this.querySelectorAll('[data-sss-answer]')) {
      const v = this.state.answers[input.dataset.sssAnswer];
      input.checked = input.type === 'radio' ? v === input.value : Boolean(v);
    }
    if (r.lookupInput) r.lookupInput.value = this.state.lookupText;
  }

  /* ----- Mode, unit and readings ----- */

  #setMode(mode, { focus = false } = {}) {
    if (mode !== 'measure' && mode !== 'lookup') return;
    if (this.#boot !== 'ready') {
      // The tabs are server-rendered: remember the choice and show that we are still loading.
      this.pendingMode = mode;
      this.querySelector('.sss-boot')?.classList.remove('sss-visually-hidden');
      return;
    }
    if (mode !== this.state.mode) this.toolsMsg = null; // share message belongs to its mode
    this.state.mode = mode;
    this.dataset.sssMode = mode;
    for (const btn of this.querySelectorAll('[data-action="mode"]')) {
      btn.setAttribute('aria-expanded', String(btn.dataset.mode === mode));
    }
    // Each mode has a panel in the tool card and a results pane under both columns.
    for (const pane of this.querySelectorAll('[data-sss-pane]')) pane.hidden = pane.dataset.sssPane !== mode;
    if (mode === 'measure') this.#update({ announce: false });
    else this.#updateLookup();
    if (focus) {
      const target = mode === 'measure' ? this.querySelector('[data-sss-reading="0"]') : this.refs.lookupInput;
      target?.focus();
    }
  }

  /** Display unit only: typed values keep their unit, so a switch never changes a verdict (golden 9b). */
  #setUnit(unit) {
    if (unit !== 'in' && unit !== 'mm') return;
    this.state.unit = unit;
    local.set('sss:unit', unit);
    this.#syncInputs();
  }

  /** A typed value as shown in the display unit. */
  #shown(value, unit = this.state.unit) {
    const t = String(value?.text ?? '');
    return !t.trim() || value.unit === unit || !this.core ? t : this.core.convertReadingText(t.trim(), value.unit, unit);
  }

  #addReading() {
    if (this.state.readings.length >= MAX_READINGS) return;
    this.state.readings.push(src());
    const i = this.state.readings.length - 1;
    this.refs.readings.insertAdjacentHTML('beforeend', this.#readingFieldHtml(i));
    this.refs.addReading.hidden = this.state.readings.length >= MAX_READINGS;
    this.querySelector(`[data-sss-reading="${i}"]`)?.focus();
  }

  #parseDistance(text, unit) {
    if (/^0+(?:[.,]0*)?$/.test(text)) return { ok: true, mm: 0 };
    const r = this.core.parseReading(text, unit);
    return r.ok ? { ok: true, mm: r.mm } : { ok: false, error: r.error };
  }

  /* ----- View model ----- */

  #compute() {
    const { core, data } = this;
    const s = this.state;
    const unit = s.unit;
    const fields = s.readings.map((r) => (filled(r) ? core.parseReading(r.text, r.unit) : null));
    const ok = fields.filter((p) => p && p.ok);
    const summary = ok.length ? core.summarizeReadings(ok) : null;
    const vm = { unit, fields, ok, summary, geo: this.#geoInput(), answers: { ...s.answers }, design: this.design, groups: [], readingMistakes: [] };
    vm.blocked = BLOCK_KEYS.filter((k) => s.answers[k]);
    if (!summary) return vm;

    // Judge the mean in the unit it was typed in (the display unit when mixed).
    const mu = ok.every((p) => p.unit === ok[0].unit) ? ok[0].unit : unit;
    const meanInUnit = core.fromMm(summary.mean_mm, mu);
    const mistake = core.detectUnitMistake(meanInUnit, mu, data);
    if (mistake) {
      const key = `${mistake.kind}:${mu}:${meanInUnit.toFixed(4)}`;
      if (key !== this.#dismissedMistake) vm.mistake = { ...mistake, key, value: meanInUnit, unit: mu };
    } else if (ok.length > 1) {
      // One wrong-unit or slipped-decimal reading hides in a plausible mean (golden 22b).
      fields.forEach((p, i) => {
        const m = p && p.ok ? core.detectUnitMistake(p.value, p.unit, data) : null;
        const key = m && `r${i}:${m.kind}:${p.unit}:${p.value}`;
        if (m && !this.#dismissedReadings.has(key)) vm.readingMistakes.push({ ...m, i, key, unit: p.unit });
      });
    }
    vm.hold = Boolean(vm.mistake?.suggest || vm.readingMistakes.length);
    if (vm.hold) return vm;

    vm.match = core.matchSleeves(summary.mean_mm, data);
    vm.groups = (vm.match.groups || []).map((g) => this.#groupVm(g, vm));
    const entries = vm.groups.flatMap((g) => g.entries);
    vm.count = entries.length;
    vm.spread = entries.some((e) => e.spread);
    vm.sure = entries.filter((e) => (e.state === 'fits' || e.state === 'near_edge') && !e.spread).length;
    return vm;
  }

  #geoInput() {
    const values = {};
    const errors = {};
    for (const key of GEO_KEYS) {
      const g = this.state.geo[key];
      if (!filled(g)) continue;
      const r = this.#parseDistance(g.text.trim(), g.unit);
      // o = 0 is not "nothing stays on".
      if (key === 'o' && (r.ok ? r.mm <= 0 : r.error === 'not_positive')) errors.o = 'o_positive';
      else if (r.ok) values[key] = r.mm;
      else errors[key] = r.error === 'not_positive' ? 'not_positive' : 'invalid';
    }
    if (values.tn != null && values.tf != null && values.tf <= values.tn) errors.tf = 'order';
    const complete = values.tn != null && values.tf != null && !Object.keys(errors).length;
    const input = complete ? { tn: values.tn, tf: values.tf } : null;
    if (input && values.c != null) input.c = values.c;
    if (input && values.o != null) input.o = values.o;
    return { input, values, errors };
  }

  #groupVm(group, vm) {
    const entries = group.parts.map(({ part, state, margins }) => ({
      part, state, margins,
      listing: this.#listingFor(part.pn),
      geometry: vm.geo.input ? this.core.checkGeometry(part, vm.geo.input) : null,
      // The mean of disagreeing readings is a diameter nobody measured.
      spread: this.core.spreadWarning(vm.summary, part),
    }));
    const byPn = new Map(entries.map((e) => [e.part.pn, e]));
    const sorted = this.core.sortParts(entries.map((e) => ({
      part: e.part, state: e.state, geometry: e.geometry,
      listingStatus: LISTING_DECIDED.has(e.listing.status) ? e.listing.status : 'available',
    })), { preferGold: Boolean(vm.answers.a) }).map((x) => byPn.get(x.part.pn)).filter(Boolean);
    for (const e of sorted) e.action = this.#actionFor(e, vm);
    return {
      group,
      entries: sorted,
      spread: entries.some((e) => e.spread),
      hasGold: entries.some((e) => e.part.type === 'GOLD'),
    };
  }

  #actionFor(entry, vm) {
    if (this.design) return { action: 'design', reason: 'design' };
    const base = {
      state: entry.state, part: entry.part, blocked: vm.blocked.length > 0, nominal: Boolean(vm.summary?.nominal),
      spread: Boolean(entry.spread), allowCartBorderline: this.config.allowCartBorderline,
      geometryStatus: entry.geometry?.status ?? null,
    };
    const status = entry.listing.status;
    if (!LISTING_DECIDED.has(status)) {
      const pre = this.core.decideAction({ ...base, listingStatus: 'available' });
      if (pre.action !== 'cart') return pre;
      return { action: status === 'degraded' ? 'check' : 'pending', reason: pre.reason };
    }
    return this.core.decideAction({ ...base, listingStatus: status });
  }

  /** Blocking answers stop the cart here too (plan: acceptance 5). */
  #lookupAction(part) {
    const listing = this.#listingFor(part.pn);
    const entry = { part, listing, geometry: null };
    const blocked = BLOCK_KEYS.filter((k) => this.state.answers[k]);
    return { entry, blocked, action: this.#actionFor(entry, { blocked, summary: null }) };
  }

  /* ----- Rendering: measure mode ----- */

  #refresh() {
    if (this.#boot !== 'ready') return;
    if (this.state.mode === 'measure') this.#update({ announce: false });
    if (this.state.mode === 'lookup') this.#updateLookup();
  }

  /** Measure view model + region HTML, no DOM access (used by tests). */
  sssView() {
    const vm = this.#compute();
    return {
      vm,
      prompt: this.#promptHtml(vm),
      results: this.#resultsHtml(vm),
      checkOut: this.#checkOutHtml(vm),
      checklist: vm.summary && !vm.hold ? this.#checklistHtml(vm) : '',
    };
  }

  #update({ announce = true, announceNow = false } = {}) {
    if (this.#boot !== 'ready') return;
    const view = this.sssView();
    const vm = view.vm;
    this.lastVm = vm;
    this.#renderFieldErrors(vm);
    if (this.refs.errors.firstElementChild && !this.#readingErrors(vm).length) this.#swap(this.refs.errors, '');
    this.#swap(this.refs.prompt, view.prompt);
    this.#swap(this.refs.results, view.results);
    this.#swap(this.refs.checkOut, view.checkOut);
    this.#swap(this.refs.checklist, view.checklist);
    if (this.quotePanel && !this.quotePanel.hidden) this.#fillQuoteHidden();
    if (announce && this.state.mode === 'measure') this.#announce(this.#announceText(vm), announceNow);
  }

  /** Replace a region's HTML, keeping focus on the same logical control. */
  #swap(region, html) {
    if (!region || this.#html.get(region) === html) return;
    const active = document.activeElement;
    const key = region.contains(active) ? active.dataset.focusKey : null;
    const hadFocus = region.contains(active);
    region.innerHTML = html;
    this.#html.set(region, html);
    if (!hadFocus) return;
    // Same control, else another control for the same part.
    const pn = key ? key.slice(key.lastIndexOf(':') + 1) : '';
    const next = key ? region.querySelector(`[data-focus-key="${CSS.escape(key)}"]`)
      || (pn && (region.querySelector(`[data-focus-key="add:${CSS.escape(pn)}"]`)
        || region.querySelector(`.sss-part__buy [data-focus-key$=":${CSS.escape(pn)}"]`))) : null;
    if (next) next.focus({ preventScroll: true });
    else (region.querySelector('[data-sss-focus-heading]') || this.#resultsHeading())?.focus({ preventScroll: true });
  }

  #readingErrors(vm) {
    if (!vm) return [];
    const out = [];
    vm.fields.forEach((f, i) => {
      let code = f && !f.ok ? f.error : null;
      if (!f && i === 0 && this.submitted) code = 'empty';
      if (code) out.push({ id: `${this.idBase}-r${i}`, text: `Reading ${i + 1}: ${READING_ERRORS[code] || READING_ERRORS.invalid}`, code, i });
    });
    return out;
  }

  #renderFieldErrors(vm) {
    if (!vm) return;
    const errs = new Map(this.#readingErrors(vm).map((e) => [e.i, e]));
    const odd = new Set((vm.readingMistakes || []).map((m) => m.i));
    this.state.readings.forEach((_, i) => {
      const e = this.touched.has(`r${i}`) ? errs.get(i) : null;
      const msg = e ? READING_ERRORS[e.code] || READING_ERRORS.invalid
        : (odd.has(i) && this.touched.has(`r${i}`) ? 'Check this reading: see the question below.' : '');
      this.#setFieldError(`${this.idBase}-r${i}`, msg);
    });
    for (const key of GEO_KEYS) {
      const code = this.touched.has(key) ? vm.geo.errors[key] : null;
      this.#setFieldError(`${this.idBase}-${key}`, code ? GEO_ERRORS[code] : '');
    }
  }

  #setFieldError(id, message) {
    const input = document.getElementById(id);
    const box = document.getElementById(`${id}-error`);
    if (!input || !box) return;
    if (message) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
    box.textContent = message;
    box.hidden = !message;
  }

  #errorSummaryHtml(title, errors, level) {
    return `
      <div class="sss-errors" role="alert" tabindex="-1">
        <h${level} class="sss-errors__heading">${esc(title)}</h${level}>
        <ul class="sss-errors__list">${errors.map((e) => `<li><a href="#${esc(e.id)}">${esc(e.text)}</a></li>`).join('')}</ul>
      </div>`;
  }

  #len(mm, unit, decimals) {
    return this.core.formatLength(mm, unit, decimals == null ? undefined : { decimals });
  }

  #meanText(vm) {
    const { summary, unit } = vm;
    if (!summary) return '';
    if (summary.nominal) {
      const v = Number(this.core.fromMm(summary.mean_mm, unit).toFixed(unit === 'in' ? 3 : 2));
      return `${v} ${unit}`;
    }
    // Typed in the other unit: one more decimal (1.0870 in = 27.6098 mm, not 27.610).
    const other = vm.ok?.some((p) => p.unit !== unit);
    return this.#len(summary.mean_mm, unit, other ? (unit === 'in' ? 5 : 4) : undefined);
  }

  #promptHtml(vm) {
    const m = vm.mistake;
    if (!m) return vm.readingMistakes.map((rm) => this.#readingPromptHtml(rm)).join('');
    const typed = `${Number(m.value.toFixed(4))}`;
    const id = `${this.idBase}-prompt`;
    if (!m.suggest) {
      return `<div class="sss-note sss-note--warn" role="note"><p><strong>Check that reading.</strong> ${esc(typed)} mm is larger than any SKF wear sleeve (up to ${esc(this.#len(this.data.zones.ldslv_max_mm, 'mm', 0))}).</p></div>`;
    }
    const alt = `${Number(Number(m.suggest.value).toFixed(4))} ${m.suggest.unit}`;
    const altOther = this.#len(this.core.toMm(m.suggest.value, m.suggest.unit), otherUnit(m.suggest.unit));
    const copy = {
      inch_ldslv_or_mm: [`${typed} in is bigger than any Speedi-Sleeve. Is it a ${typed} in shaft, or did you mean ${alt}?`, `Yes, ${alt} (${altOther})`, `No, it's a ${typed} in shaft (LDSLV)`],
      inch_too_large: [`${typed} in is larger than any SKF wear sleeve. Did you mean ${alt}?`, `Yes, ${alt}`, 'No, keep inches'],
      mm_too_small: [`${typed} mm is smaller than the smallest Speedi-Sleeve. Did you mean ${alt}?`, `Yes, ${alt}`, 'No, keep millimetres'],
    }[m.kind] || [`Did you mean ${alt}?`, `Yes, ${alt}`, 'No'];
    return `
      <div class="sss-prompt" role="group" aria-labelledby="${id}">
        <p class="sss-prompt__text" id="${id}"><strong>Check the unit.</strong> ${esc(copy[0])}</p>
        <div class="sss-prompt__actions">
          <button type="button" class="button button--primary sss-btn sss-btn--primary" data-action="mistake-accept" data-focus-key="mistake-accept">${esc(copy[1])}</button>
          <button type="button" class="button button--secondary sss-btn sss-btn--secondary" data-action="mistake-dismiss" data-focus-key="mistake-dismiss">${esc(copy[2])}</button>
        </div>
      </div>`;
  }

  #readingPromptHtml(m) {
    const n = m.i + 1;
    const text = String(this.state.readings[m.i]?.text || '').trim();
    const typed = `${text} ${m.unit}`;
    const alt = m.suggest ? `${text} ${m.suggest.unit}` : '';
    const id = `${this.idBase}-rp${m.i}`;
    const what = m.kind === 'mm_too_small' ? 'smaller than any Speedi-Sleeve'
      : m.kind === 'inch_ldslv_or_mm' ? 'bigger than any Speedi-Sleeve' : 'larger than any SKF wear sleeve';
    return `
      <div class="sss-prompt" role="group" aria-labelledby="${id}">
        <p class="sss-prompt__text" id="${id}"><strong>Check reading ${n}.</strong> ${esc(typed)} is ${what} in ${m.unit === 'in' ? 'inches' : 'millimetres'}. <a href="#${this.idBase}-r${m.i}">Fix it</a> if it's a typo${m.suggest ? `, or is it ${esc(alt)}?` : '.'}</p>
        <div class="sss-prompt__actions">
          ${m.suggest ? `<button type="button" class="button button--primary sss-btn sss-btn--primary" data-action="reading-accept" data-index="${m.i}" data-focus-key="ra:${m.i}">Yes, ${esc(alt)}</button>` : ''}
          <button type="button" class="button button--secondary sss-btn sss-btn--secondary" data-action="reading-dismiss" data-index="${m.i}" data-focus-key="rd:${m.i}">Keep ${esc(typed)}</button>
        </div>
      </div>`;
  }

  #readingMistake(i, accept) {
    const m = this.lastVm?.readingMistakes?.find((x) => x.i === i);
    if (!m) return;
    if (!accept) this.#dismissedReadings.add(m.key);
    else if (m.suggest) {
      this.state.readings[i] = src(this.state.readings[i].text, m.suggest.unit);
      this.#syncInputs();
    }
    this.#update({ announceNow: true });
    const next = this.refs.prompt.querySelector('[data-action="reading-accept"], [data-action="reading-dismiss"]');
    if (next) next.focus({ preventScroll: true });
    else this.#focusResults();
  }

  /** Values typed in the old unit are read in the suggested one. */
  #acceptMistake() {
    const m = this.lastVm?.mistake;
    if (!m?.suggest) return;
    const re = (v) => (v.unit === m.unit ? src(v.text, m.suggest.unit) : v);
    this.state.readings = this.state.readings.map(re);
    for (const k of GEO_KEYS) this.state.geo[k] = re(this.state.geo[k]);
    this.#setUnit(m.suggest.unit);
    this.#update({ announceNow: true });
    this.#focusResults();
  }

  #showAnswers() {
    this.#setMode('measure');
    if (this.refs.check) { this.refs.check.open = true; this.openDetails.add('check'); }
    (this.querySelector('[data-sss-answer]:checked') || this.querySelector('[data-sss-answer]'))?.focus();
  }

  #dismissMistake() {
    const m = this.lastVm?.mistake;
    if (!m) return;
    this.#dismissedMistake = m.key;
    this.#update({ announceNow: true });
    this.#focusResults();
  }

  #resultsHeading() {
    return this.refs?.results.querySelector('.sss-results__heading') || null;
  }

  #focusResults() {
    const h = this.#resultsHeading();
    if (h) {
      h.focus({ preventScroll: true });
      h.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
  }

  #resultsHtml(vm) {
    if (!vm.summary || vm.hold) return '';
    const id = this.idBase;
    const n = vm.summary.n;
    const mean = this.#meanText(vm);
    const zone = vm.match?.zone;
    const readingsNote = n >= 3 ? '' : `<p class="sss-note sss-note--info">Based on ${n} ${plural(n, 'reading', 'readings')}. ${n === 1 ? 'Add 2 more' : 'Add a third'} for a reliable fit.</p>`;
    const meta = `<p class="sss-results__meta">Average of ${n} ${plural(n, 'reading', 'readings')}${n > 1 ? `, spread ${esc(this.#len(vm.summary.spread_mm, vm.unit))}` : ''}.</p>`;
    const designNote = vm.design ? '<p class="sss-note sss-note--info">Sample results. Prices and stock show on the live page.</p>' : '';

    const eyebrow = '<p class="sss-eyebrow sss-eyebrow--line" aria-hidden="true">Your results</p>';
    if (zone !== 'speedi') {
      return `
        <section class="sss-results" aria-labelledby="${id}-results-h">
          ${eyebrow}${this.#noMatchHtml(vm, mean)}
          ${meta}${readingsNote}
          ${this.#nearMissHtml(vm)}
          ${this.#toolsHtml()}
        </section>`;
    }

    const heading = this.#headline(vm, mean);
    const unitStep = vm.unit === 'in' ? '0.001 in' : '0.01 mm';
    return `
      <section class="sss-results" aria-labelledby="${id}-results-h">
        ${eyebrow}<h3 class="sss-results__heading" id="${id}-results-h" tabindex="-1">${esc(heading)}</h3>
        ${meta}${designNote}${readingsNote}
        ${vm.summary.nominal ? `<p class="sss-note sss-note--warn">These are possible sizes only. Each size covers just 0.002–0.010 in, so measure to ${unitStep} to confirm the fit and see buying options.</p>` : ''}
        ${vm.blocked.length ? this.#blockedHtml(vm.blocked, vm.groups.flatMap((g) => g.entries.map((e) => e.part.pn))) : ''}
        ${vm.groups.length > 1 ? `<p class="sss-results__note">Your reading fits ${vm.groups.length} size ranges. Choose by width: the sleeve must cover the whole worn band.</p>` : ''}
        <p class="sss-gauge__key" aria-hidden="true">On each scale: solid band = SKF range, striped ends = near the edge, amber stripes = borderline, line = your average${n > 1 ? ', bracket = spread of your readings' : ''}.</p>
        ${vm.groups.map((g, i) => this.#groupHtml(g, vm, i)).join('')}
        ${this.#excludedHtml(vm, 'secondary')}
        ${this.#nearMissHtml(vm)}
        ${this.#toolsHtml()}
      </section>`;
  }

  /** Excluded parts (99057) holding the reading: confirm with us (golden 24). */
  #excludedHtml(vm, cls) {
    const pns = (vm.match?.excludedHits || []).map((e) => e.part.pn);
    if (!pns.length || vm.design) return '';
    const one = pns.length === 1;
    return `<div class="sss-note sss-note--info"><p>SKF lists ${one ? 'part' : 'parts'} ${esc(pns.join(', '))} for this size, but we need to confirm ${one ? 'it' : 'them'} with SKF before we can recommend ${one ? 'it' : 'them'}.</p>${this.#quoteButton('excluded', 'Ask us to confirm', pns, cls)}</div>`;
  }

  /** "3 sleeves fit 1.7500 in"; borderline matches are never counted as fits. */
  #headline(vm, mean) {
    if (vm.summary.nominal) return `Possible sizes for ${mean}`;
    if (vm.spread) return `Your readings vary by ${this.#len(vm.summary.spread_mm, vm.unit)}: re-measure (average ${mean})`;
    const border = vm.count - vm.sure;
    if (!vm.sure) return `${border} borderline ${plural(border, 'match', 'matches')} for ${mean}`;
    return `${vm.sure} ${plural(vm.sure, 'sleeve fits', 'sleeves fit')} ${mean}${border ? `, ${border} borderline` : ''}`;
  }

  #quoteButton(reason, label, pns = [], kind = 'secondary') {
    return `<button type="button" class="${BTN[kind]}" data-action="quote" data-reason="${esc(reason)}" data-pns="${esc(pns.join(','))}" data-focus-key="quote:${esc(reason)}:${esc(pns.join(','))}">${esc(label)}</button>`;
  }

  #blockedHtml(blocked, pns, lookup = false) {
    return `
      <div class="sss-note sss-note--block" role="note">
        <p><strong>A Speedi-Sleeve can't be fitted as described.</strong>${lookup ? ' You told us:' : ''}</p>
        <ul class="sss-note__list">${blocked.map((k) => `<li>${esc(BLOCK_COPY[k])}</li>`).join('')}</ul>
        <p>SKF's alternatives:</p>
        <ul class="sss-note__list">
          <li>Rework or replace the seal running surface on the shaft.</li>
          <li>Fit a spacer ring in the housing bore so the seal runs on unworn shaft.</li>
          <li>Press the new seal in to a different depth, clear of the worn band.</li>
        </ul>
        ${this.#quoteButton('blocked', 'Ask us about options', pns)}
        ${lookup ? '<button type="button" class="sss-btn sss-btn--link" data-action="answers">Change your answers</button>' : ''}
      </div>`;
  }

  #groupHtml(gvm, vm, index) {
    const { group } = gvm;
    const unit = vm.unit;
    const alt = otherUnit(unit);
    const hid = `${this.idBase}-g${index}`;
    const range = (pair, u) => `${fixed(pair[0], u)}–${fixed(pair[1], u)} ${u}`;
    const main = unit === 'in' ? group.display_in : group.display_mm;
    const other = unit === 'in' ? group.display_mm : group.display_in;
    const goldPns = gvm.entries.map((e) => e.part.pn);
    const n = goldPns.length;
    // Column heads for the desktop grid; each cell also carries its own label for phones and screen readers.
    const cols = ['Part', 'Type', 'Covers', 'Total width', 'Flange OD', 'Price', 'Order'];
    return `
      <section class="sss-group${this.config.showPrices ? '' : ' sss-group--noprice'}" aria-labelledby="${hid}">
        <div class="sss-group__head">
          <h4 class="sss-group__heading" id="${hid}">Fits shafts <span class="sss-num">${esc(range(main, unit))}</span> <span class="sss-group__alt sss-num">(${esc(range(other, alt))})</span></h4>
          <p class="sss-group__count">${n} ${plural(n, 'part', 'parts')}</p>
        </div>
        ${this.#gaugeHtml(gvm, vm)}
        ${gvm.spread ? `<p class="sss-note sss-note--warn">Your readings vary by ${esc(this.#len(vm.summary.spread_mm, unit))}, more than this size range is wide. Re-measure before you order (Apollo guidance).</p>` : ''}
        ${vm.answers.a && !gvm.hasGold && !vm.design ? `<div class="sss-note sss-note--info"><p>No stock Gold part for this size. Gold on request:</p>${this.#quoteButton('gold_on_request', 'Ask for Gold', goldPns)}</div>` : ''}
        <div class="sss-parts__head" aria-hidden="true">${cols.map((c) => `<span>${c}</span>`).join('')}</div>
        <ul class="sss-parts" role="list">${gvm.entries.map((e) => this.#partRowHtml(e, vm)).join('')}</ul>
      </section>`;
  }

  #gaugeHtml(gvm, vm) {
    const { group } = gvm;
    const s = vm.summary;
    const unit = vm.unit;
    const [omin, omax] = group.outer;
    const conf = group.conf;
    const width = Math.max(omax - omin, 1e-6);
    // Range plus readings, at most 1.5 range-widths either side.
    const lo0 = Math.max(Math.min(omin, s.min_mm), omin - 1.5 * width);
    const hi0 = Math.min(Math.max(omax, s.max_mm), omax + 1.5 * width);
    const pad = (hi0 - lo0) * 0.2;
    const lo = lo0 - pad;
    const hi = hi0 + pad;
    const pct = (x) => Math.min(100, Math.max(0, ((x - lo) / (hi - lo)) * 100)).toFixed(2);
    const span = (a, b) => `--a:${pct(a)}%;--w:${(pct(b) - pct(a)).toFixed(2)}%`;
    const e = conf ? Math.min(this.core.edgeBand(gvm.entries[0].part), (conf[1] - conf[0]) / 2) : 0;
    const refMin = conf ? conf[0] : omin;
    const refMax = conf ? conf[1] : omax;
    const below = s.mean_mm - refMin;
    const above = refMax - s.mean_mm;
    const mean = this.#meanText(vm);
    // Under the last shown digit: "0.0000 in above" would contradict the label (golden 23).
    const tiny = this.core.toMm(unit === 'in' ? 0.00005 : 0.0005, unit);
    let text;
    if (below >= 0 && above >= 0) {
      text = `Your average, ${mean}, is ${this.#len(below, unit)} above the minimum and ${this.#len(above, unit)} below the maximum${conf ? '' : ' (outer limits)'}.`;
    } else if (Math.min(below, above) > -tiny) {
      text = `Your average, ${mean}, is at the edge of SKF's published range.`;
    } else if (this.#insidePrinted(group, s.mean_mm, unit, e)) {
      // Outside the range both SKF editions agree on, but inside the printed "Fits shafts" range.
      text = `Your average, ${mean}, is inside the printed range, but the two SKF catalogues disagree ${below < 0 ? 'below' : 'above'} ${this.#len(below < 0 ? refMin : refMax, unit)}.`;
    } else if (below < 0) {
      text = `Your average, ${mean}, is ${this.#len(-below, unit)} below SKF's current minimum.`;
    } else {
      text = `Your average, ${mean}, is ${this.#len(-above, unit)} above SKF's current maximum.`;
    }
    return `
      <div class="sss-gauge">
        <div class="sss-gauge__scale" aria-hidden="true">
          <span class="sss-gauge__outer" style="${span(omin, omax)}"></span>
          ${conf ? `<span class="sss-gauge__conf" style="${span(conf[0], conf[1])}"></span>` : ''}
          ${e > 0 ? `<span class="sss-gauge__edge" style="${span(conf[0], conf[0] + e)}"></span><span class="sss-gauge__edge" style="${span(conf[1] - e, conf[1])}"></span>` : ''}
          ${s.n > 1 ? `<span class="sss-gauge__spread" style="${span(s.min_mm, s.max_mm)}"></span>` : ''}
          <span class="sss-gauge__needle" style="--a:${pct(s.mean_mm)}%"></span>
          <span class="sss-gauge__val" style="--a:${pct(s.mean_mm)}%">${esc(mean.split(' ')[0])}</span>
          <span class="sss-gauge__end" style="--a:${pct(refMin)}%">${esc(fixed(this.core.fromMm(refMin, unit), unit))}</span>
          <span class="sss-gauge__end sss-gauge__end--max" style="--a:${pct(refMax)}%">${esc(fixed(this.core.fromMm(refMax, unit), unit))}</span>
        </div>
        <p class="sss-gauge__text">${esc(text)}</p>
      </div>`;
  }

  #insidePrinted(group, mean_mm, unit, band_mm) {
    const [lo, hi] = unit === 'mm' ? group.display_mm : group.display_in;
    const v = this.core.fromMm(mean_mm, unit);
    const band = this.core.fromMm(band_mm, unit);
    return v - lo > band + 1e-9 && hi - v > band + 1e-9;
  }

  // True when the mean sits clearly inside the printed range (more than the part's edge band from either limit).
  #insideDisplayRange(p, mean_mm, unit) {
    const [lo, hi] = unit === 'mm' ? p.range.display_mm : p.range.display_in;
    const v = this.core.fromMm(mean_mm, unit);
    const band = this.core.fromMm(this.core.edgeBand(p), unit);
    return v - lo > band + 1e-9 && hi - v > band + 1e-9;
  }

  #partRowHtml(e, vm) {
    const p = e.part;
    const pn = p.pn;
    const unit = vm.unit;
    const pick = vm.answers.a && p.type === 'GOLD';
    let st = vm.summary.nominal
      ? { cls: 'info', icon: '~', text: 'Possible size — measure to confirm' }
      : STATE_COPY[e.spread ? 'spread' : e.state] || STATE_COPY.fits;
    // Borderline inside the printed "Fits shafts" range means the two SKF editions disagree, not "at the edge".
    if (st === STATE_COPY.borderline && this.#insideDisplayRange(p, vm.summary.mean_mm, unit)) st = BORDERLINE_DISAGREE;
    const badge = `<p class="sss-badge sss-badge--${st.cls}"><span class="sss-badge__icon" aria-hidden="true">${st.icon}</span>${esc(st.text)}</p>`;
    const notes = [
      p.single_source && !p.whitelisted ? '<p class="sss-part__note">Listed in only one SKF edition — check availability.</p>' : '',
      p.type === 'UNCONFIRMED' ? '<p class="sss-part__note">SKF sources disagree on whether this is Standard or Gold. We will confirm.</p>' : '',
      this.#geoNoteHtml(e.geometry, unit),
    ].join('');
    const detailsKey = `part:${pn}`;
    const { action, reason } = e.action;
    // Price in its own column (cart, or a sold-out original); the buy cell keeps stock, quantity and buttons.
    const price = action === 'cart' || (action === 'quote' && reason === 'sold_out') ? this.#priceHtml(e.listing.listing) : '';
    return `
      <li class="sss-part${pick ? ' sss-part--pick' : ''}">
        <div class="sss-part__main">
          <div class="sss-part__id">
            <span class="sss-part__pn">SKF ${esc(pn)}</span>
            ${badge}
          </div>
          <div class="sss-part__type">
            <span class="sss-type sss-type--${esc(String(p.type).toLowerCase())}">${esc(TYPE_SHORT[p.type] || p.type)}</span>
            ${pick ? '<span class="sss-type sss-type--pick">Suggested for abrasive duty</span>' : ''}
          </div>
          ${this.#dimCell(p, 'b1', unit, 'Covers')}${this.#dimCell(p, 'b', unit, 'Total width')}${this.#dimCell(p, 'D', unit, 'Flange OD')}
          <div class="sss-part__price">${price}</div>
          <div class="sss-part__buy">${this.#buyHtml(e, e.action, unit, 'm', false)}</div>
        </div>
        ${notes ? `<div class="sss-part__notes">${notes}</div>` : ''}
        <details class="sss-part__details" data-sss-details="${detailsKey}"${this.openDetails.has(detailsKey) ? ' open' : ''}>
          <summary class="sss-part__summary" data-focus-key="details:${esc(pn)}">Sizes and details<span class="sss-visually-hidden"> for SKF ${esc(pn)}</span></summary>
          ${this.#specsHtml(p, unit, { listing: e.listing.listing })}
        </details>
      </li>`;
  }

  #geoNoteHtml(geo, unit) {
    if (!geo || geo.status === 'incomplete' || geo.status === 'invalid') return '';
    if (geo.status === 'infeasible') {
      const why = (geo.reasons || []).map((r) => GEO_REASON[r]).filter(Boolean).join('; ');
      return `<p class="sss-part__note sss-part__note--bad"><span aria-hidden="true">× </span>Won't fit here: ${esc(why || 'the worn band position does not suit this width')}. Try another width or ask us.</p>`;
    }
    const d = unit === 'in' ? 3 : 2;
    const place = `Push it on until the flange-side edge is ${this.#len(geo.place_mm, unit, d)} from the shaft end (outer edge at ${this.#len(geo.outer_edge_mm, unit, d)}).`;
    if (geo.status === 'pipe') {
      return `<p class="sss-part__note sss-part__note--warn"><span aria-hidden="true">! </span>Covers your worn band, but the included tool is too short for this depth. Use a pipe with the same inside diameter as the tool, with a square, burr-free end. ${esc(place)}</p>`;
    }
    return `<p class="sss-part__note sss-part__note--ok"><span aria-hidden="true">✓ </span>Covers your worn band. ${esc(place)}</p>`;
  }

  #priceHtml(listing) {
    if (!this.config.showPrices || !listing?.pf) return '';
    const compare = listing.cf ? `<s class="sss-buy__compare"><span class="sss-visually-hidden">Regular price </span>${esc(listing.cf)}</s> ` : '';
    return `<p class="sss-buy__price">${compare}<span class="sss-visually-hidden">${listing.cf ? 'Sale price ' : 'Price '}</span>${esc(listing.pf)}</p>`;
  }

  #buyHtml(entry, action, unit, scope, withPrice = true) {
    const pn = entry.part.pn;
    const L = entry.listing || {};
    const li = L.listing;
    const msgHtml = this.#cartMsgHtml(pn);
    switch (action.action) {
      case 'cart': {
        const [min = 1, step = 1, max = null] = Array.isArray(li?.q) ? li.q : [];
        const busy = this.cartBusy.has(pn);
        const qid = `${this.idBase}-q${scope}-${pn}`;
        const value = this.qty.get(pn) ?? min;
        return `
          ${withPrice ? this.#priceHtml(li) : ''}
          <p class="sss-buy__stock">Available</p>
          <div class="sss-buy__row">
            <label class="sss-visually-hidden" for="${qid}">Quantity of SKF ${esc(pn)}</label>
            <input class="sss-buy__qty" id="${qid}" type="number" inputmode="numeric" min="${esc(min)}" step="${esc(step || 1)}"${max ? ` max="${esc(max)}"` : ''} value="${esc(value)}" data-sss-qty="${esc(pn)}" data-focus-key="qty:${esc(pn)}">
            <button type="button" class="button button--primary sss-btn sss-btn--primary sss-btn--buy" data-action="add" data-pn="${esc(pn)}" data-focus-key="add:${esc(pn)}"${busy ? ' disabled aria-busy="true"' : ''}><span data-sss-label>${busy ? 'Adding…' : 'Add to cart'}</span><span class="sss-visually-hidden"> SKF ${esc(pn)}</span></button>
          </div>
          ${action.reason === 'near_edge' ? this.#quoteButton('confirm_fit', 'Ask us to confirm', [pn], 'link') : ''}
          ${msgHtml}`;
      }
      case 'quote': {
        const [why, label] = L.dupOnly ? ["We couldn't confirm stock online", 'Ask us for stock']
          : QUOTE_WHY[action.reason] || ['Ask us about this part', 'Request a quote'];
        const search = L.failed ? ` <a class="sss-buy__link" href="${esc(this.config.routes.search_url)}?q=${encodeURIComponent(pn)}">See price</a>` : '';
        return `${withPrice && action.reason === 'sold_out' ? this.#priceHtml(li) : ''}<p class="sss-buy__why">${esc(why)}${search}</p>${this.#quoteButton(action.reason, label, [pn])}`;
      }
      case 'pending':
        return L.status === 'checking'
          ? `<button type="button" class="button button--secondary sss-btn sss-btn--secondary" aria-disabled="true" aria-busy="true" data-focus-key="check:${esc(pn)}">Checking price…</button>`
          : '<p class="sss-buy__pending">Checking price…</p>';
      case 'check':
        return `<button type="button" class="button button--secondary sss-btn sss-btn--secondary" data-action="check-price" data-pn="${esc(pn)}" data-focus-key="check:${esc(pn)}">Check price and stock<span class="sss-visually-hidden"> for SKF ${esc(pn)}</span></button>`;
      case 'design':
        return '<p class="sss-buy__why">Price and stock show on the live page.</p>';
      default: {
        const why = {
          blocked: 'Not suitable for this shaft — see above.',
          nominal: `Measure to ${unit === 'in' ? '0.001 in' : '0.01 mm'} to see buying options.`,
          spread: 'Your readings disagree by more than this size range. Re-measure before you order.',
          just_outside: 'Does not fit.',
          no_fit: 'Does not fit.',
        }[action.reason] || 'Not available here.';
        return `<p class="sss-buy__why">${esc(why)}</p>${action.reason === 'spread' ? this.#quoteButton('confirm_fit', 'Ask us to check', [pn], 'link') : ''}`;
      }
    }
  }

  #dimText(part, key, unit) {
    const g = part.geom || {};
    const alt = otherUnit(unit);
    const a = g[`${key}_${unit}`];
    const b = g[`${key}_${alt}`];
    if (a == null && b == null) return 'not published';
    if (a == null) return `${fixed(b, alt)} ${alt}`;
    return b == null ? `${fixed(a, unit)} ${unit}` : `${fixed(a, unit)} ${unit} (${fixed(b, alt)} ${alt})`;
  }

  /** One dimension cell of a part row: value in the display unit, the other unit underneath. */
  #dimCell(part, key, unit, label) {
    const g = part.geom || {};
    const alt = otherUnit(unit);
    const a = g[`${key}_${unit}`];
    const b = g[`${key}_${alt}`];
    const main = a != null ? `${fixed(a, unit)} ${unit}` : b != null ? `${fixed(b, alt)} ${alt}` : 'not published';
    return `<p class="sss-part__dim"><span class="sss-part__label">${label} </span><span class="sss-num">${main}</span>${a != null && b != null ? ` <span class="sss-part__alt sss-num">${fixed(b, alt)} ${alt}</span>` : ''}</p>`;
  }

  #rangeText(part, unit) {
    const r = part.range || {};
    const alt = otherUnit(unit);
    const a = unit === 'in' ? r.display_in : r.display_mm;
    const b = unit === 'in' ? r.display_mm : r.display_in;
    const txt = (pair, u) => (Array.isArray(pair) ? `${fixed(pair[0], u)}–${fixed(pair[1], u)} ${u}` : '');
    return b ? `${txt(a, unit)} (${txt(b, alt)})` : txt(a, unit);
  }

  #specsHtml(part, unit, { listing = null, twinButton = false } = {}) {
    const twin = part.twin ? this.partsByPn.get(part.twin) : null;
    const twinLabel = twin ? `${TYPE_SHORT[twin.type] || 'Twin'} version` : 'Twin';
    const twinValue = part.twin
      ? (twinButton
        ? `<button type="button" class="sss-btn sss-btn--link" data-action="lookup-pn" data-pn="${esc(part.twin)}">SKF ${esc(part.twin)}</button>`
        : `SKF ${esc(part.twin)}`)
      : '';
    const rows = [
      ['Fits shafts', esc(this.#rangeText(part, unit))],
      ['Covers (width on shaft)', esc(this.#dimText(part, 'b1', unit))],
      ['Total width incl. removable flange', esc(this.#dimText(part, 'b', unit))],
      ['Flange OD', esc(this.#dimText(part, 'D', unit))],
      ['Max install depth with included tool', esc(this.#dimText(part, 'B', unit))],
      ['Type', esc(TYPE_LABEL[part.type] || part.type)],
      part.twin ? [esc(twinLabel), twinValue] : null,
      part.nominal_label ? ['SKF nominal size', esc(part.nominal_label)] : null,
      ['SKF source', esc((part.sources || []).join(', '))],
    ].filter(Boolean);
    const link = listing?.u || listing?.h
      ? `<p class="sss-specs__link"><a href="${esc(safePath(listing.u, `${this.config.routes.root_url.replace(/\/?$/, '/')}products/${listing.h}`))}">View the product page<span class="sss-visually-hidden"> for SKF ${esc(part.pn)}</span></a></p>`
      : '';
    return `<dl class="sss-specs">${rows.map(([k, v], i) => `<div class="sss-specs__row"><dt>${k}</dt><dd${i < 5 ? ' class="sss-num"' : ''}>${v}</dd></div>`).join('')}</dl>${link}`;
  }

  #nearMissHtml(vm) {
    const misses = vm.match?.nearMisses || [];
    if (!misses.length) return '';
    return `
      <div class="sss-nearmiss">
        <p class="sss-nearmiss__title">Close to your reading, but does not fit:</p>
        <ul class="sss-nearmiss__list">${misses.map((m) => `<li><span class="sss-badge__icon" aria-hidden="true">×</span> SKF ${esc(m.part.pn)} fits ${esc(this.#rangeText(m.part, vm.unit))}. Does not fit — re-measure.</li>`).join('')}</ul>
      </div>`;
  }

  #noMatchHtml(vm, mean) {
    const z = this.data.zones || {};
    const unit = vm.unit;
    const d = unit === 'in' ? 3 : 2;
    const L = (mm) => this.#len(mm, unit, d);
    const id = `${this.idBase}-results-h`;
    const excluded = this.#excludedHtml(vm, 'primary');
    const cls = excluded ? 'secondary' : 'primary';
    const head = (text) => `<h3 class="sss-results__heading" id="${id}" tabindex="-1">${esc(excluded ? `A part may fit ${mean}: confirm with us` : text)}</h3>${excluded}`;
    const nearestPns = [vm.match.nearest?.below, vm.match.nearest?.above].filter(Boolean).flatMap((n) => n.parts.map((p) => p.pn));
    switch (vm.match.zone) {
      case 'below_min':
        return `${head(`No Speedi-Sleeve fits ${mean}`)}
          <div class="sss-nomatch"><p>The smallest Speedi-Sleeve fits shafts from ${esc(L(z.speedi_min_outer_mm))}. Ask us about other ways to repair the shaft.</p>
          ${this.#quoteButton('below_min', 'Ask for technical help', [], cls)}</div>`;
      case 'gap': {
        const side = (n, word) => (n ? `<li><span class="sss-badge__icon" aria-hidden="true">×</span> ${n.parts.map((p) => `SKF ${esc(p.pn)}`).join(', ')} fits ${esc(this.#rangeText(n.parts[0], unit))}. Does not fit: your shaft is ${esc(this.#len(n.delta_mm, unit))} ${word}.</li>` : '');
        return `${head(`No standard Speedi-Sleeve fits ${mean}`)}
          <div class="sss-nomatch">
            <p>Your reading falls between two ${excluded ? 'standard ' : ''}sizes:</p>
            <ul class="sss-nomatch__list">${side(vm.match.nearest?.below, 'larger')}${side(vm.match.nearest?.above, 'smaller')}</ul>
            <p>Your options:</p>
            <ul class="sss-note__list"><li>Rework the shaft to suit a different seal size.</li><li>Ask us about a custom-size sleeve (for volume orders).</li></ul>
            ${this.#quoteButton('gap', 'Ask about options', nearestPns, cls)}
          </div>`;
      }
      case 'no_standard':
        return `${head(`No standard sleeve fits ${mean}`)}
          <div class="sss-nomatch"><p>Speedi-Sleeves go up to ${esc(L(z.speedi_max_outer_mm))} and SKF LDSLV heavy-duty sleeves start at ${esc(L(z.ldslv_min_mm))}. Nothing standard covers the sizes in between. Ask us about options.</p>
          ${this.#quoteButton('no_standard', 'Ask about options', [], cls)}</div>`;
      case 'ldslv':
        return `${head(`${mean} needs an LDSLV heavy-duty sleeve`)}
          <div class="sss-nomatch sss-rte">${this.#slotHtml('ldslv')}
          ${this.#quoteButton('ldslv', 'Request an LDSLV quote', [], cls)}</div>`;
      default:
        return `${head(`${mean} is larger than any SKF wear sleeve`)}
          <div class="sss-nomatch"><p>SKF's LDSLV sleeves go up to ${esc(L(z.ldslv_max_mm))}. Contact us and we'll look at options.</p>
          ${this.#quoteButton('above_ldslv', 'Contact us', [], cls)}</div>`;
    }
  }

  #toolsHtml() {
    if (this.design) return '';
    const msg = this.toolsMsg;
    return `
      <div class="sss-tools">
        ${this.state.mode === 'measure' ? '<button type="button" class="button button--secondary sss-btn sss-btn--secondary" data-action="share" data-focus-key="share">Send to purchasing</button>' : ''}
        <button type="button" class="button button--secondary sss-btn sss-btn--secondary" data-action="print" data-focus-key="print">Print spec sheet</button>
        ${msg ? `<div class="sss-tools__msg" role="status">${msg.html}</div>` : ''}
      </div>`;
  }

  #checkOutHtml(vm, { print = false } = {}) {
    const out = [];
    const all = print || !vm.geo.input ? [] : vm.groups.flatMap((g) => g.entries);
    const status = all.map((e) => e.geometry?.status);
    if (all.length && !status.includes('invalid')) {
      const good = status.filter((s) => s === 'ok' || s === 'pipe').length;
      if (good) out.push(`<p>${good} of ${all.length} matching ${plural(all.length, 'sleeve covers', 'sleeves cover')} your worn band. Placement is shown with each part below.</p>`);
      else if (status.every((s) => s === 'infeasible')) out.push(`<p class="sss-part__note sss-part__note--bad">None of the matching sleeves fit this worn band position.</p>${this.#quoteButton('geometry', 'Ask about options', all.map((e) => e.part.pn))}`);
    }
    if (vm.answers.g === 'deep') out.push('<p>Groove or scoring: fill it with a powdered-metal epoxy filler and push the sleeve on before the filler cures, then clean the filler off the sleeve surface. SKF gives no depth limit; if in doubt, ask us.</p>');
    if (vm.answers.g === 'light') out.push('<p>Light track: no filler needed. You can put a thin coat of non-hardening sealant in the sleeve bore; wipe off any that squeezes out.</p>');
    if (vm.answers.f) out.push("<p>Remove the flange. Before installing, notch it: cut from the flange's outer edge into the radius at one point. After the sleeve is in place, bend it back and forth or coil it off with long-nosed pliers, without lifting the sleeve end.</p>");
    if (vm.answers.a) out.push('<p>Abrasive duty: Gold sleeves have a hard coating for grit and dirt. They are marked in the results where SKF lists one.</p>');
    return out.join('');
  }

  /** Steps from <template data-sss-tpl="steps">, filtered by data-sss-if. */
  #checklistSteps(vm) {
    const a = vm?.answers || {};
    const keep = (cond) => {
      if (!cond) return true;
      const [key, want] = cond.replace('!', '').split('=');
      const hit = want === undefined ? Boolean(a[key]) : String(a[key] || '') === want;
      return cond.startsWith('!') ? !hit : hit;
    };
    const tpl = this.querySelector('template[data-sss-tpl="steps"]');
    return tpl ? [...tpl.content.children].filter((li) => keep(li.dataset.sssIf)).map((li) => li.innerHTML) : [];
  }

  #checklistHtml(vm) {
    const intro = this.#slotHtml('checklist_heading');
    const facts = this.querySelector('template[data-sss-tpl="facts"]')?.innerHTML || '';
    return `
      <details class="sss-checklist" data-sss-details="checklist"${this.openDetails.has('checklist') ? ' open' : ''}>
        <summary class="sss-checklist__summary" data-focus-key="checklist">Installation checklist</summary>
        ${intro ? `<div class="sss-rte">${intro}</div>` : ''}
        <ol class="sss-checklist__steps">${this.#checklistSteps(vm).map((s) => `<li>${s}</li>`).join('')}</ol>
        <ul class="sss-checklist__facts">${facts}</ul>
      </details>`;
  }

  #announceText(vm) {
    if (!vm.summary) return '';
    if (vm.mistake?.suggest) return 'Check the unit before we show sizes.';
    if (vm.hold) return 'Check your readings before we show sizes.';
    const mean = this.#meanText(vm);
    const zone = vm.match?.zone;
    if (zone !== 'speedi' && vm.match?.excludedHits?.length) return `A part may fit ${mean}: confirm with us.`;
    if (zone === 'speedi') {
      const ranges = vm.groups.length > 1 ? ` in ${vm.groups.length} size ranges` : '';
      return vm.summary.nominal
        ? `${vm.count} possible ${plural(vm.count, 'size', 'sizes')} for ${mean}. Measure more precisely to confirm.`
        : `${this.#headline(vm, mean)}${ranges}.`;
    }
    return {
      below_min: `No Speedi-Sleeve fits ${mean}.`, gap: `No standard Speedi-Sleeve fits ${mean}.`,
      no_standard: `No standard sleeve fits ${mean}.`, ldslv: `${mean} needs an LDSLV heavy-duty sleeve.`,
    }[zone] || `${mean} is larger than any SKF wear sleeve.`;
  }

  #announce(message, now = false) {
    this.#pendingAnnounce = message;
    clearTimeout(this.#announceTimer);
    if (now) this.#flushAnnounce();
    else this.#announceTimer = setTimeout(() => this.#flushAnnounce(), ANNOUNCE_DELAY_MS);
  }

  #flushAnnounce() {
    clearTimeout(this.#announceTimer);
    const message = this.#pendingAnnounce;
    this.#pendingAnnounce = null;
    if (message == null) return;
    this.#trackSearch(this.lastVm);
    if (message === this.#lastAnnounced || !this.refs?.live) return;
    this.#lastAnnounced = message;
    const live = this.refs.live;
    live.textContent = '';
    requestAnimationFrame(() => { live.textContent = message; });
  }

  /* ----- Lookup mode ----- */

  #lookupPn(pn) {
    this.state.lookupText = pn;
    this.lookupSubmitted = true;
    this.#syncInputs();
    this.#setMode('lookup');
    this.refs.lookupResult.querySelector('[data-sss-focus-heading]')?.focus();
  }

  /** Lookup result + HTML, no DOM access (used by tests). */
  sssLookupView() {
    const text = this.state.lookupText.trim();
    const r = text ? this.core.lookupPart(text, this.data) : null;
    return { text, r, html: r && r.kind !== 'invalid' ? this.#lookupHtml(r, text) : '' };
  }

  #updateLookup({ announceNow = false } = {}) {
    if (this.#boot !== 'ready' || !this.refs) return;
    const { text, r, html } = this.sssLookupView();
    this.lastLookup = r ? { text, ...r } : null;
    const showInvalid = r?.kind === 'invalid' && this.lookupSubmitted;
    this.#setFieldError(`${this.idBase}-pn`, !showInvalid ? '' : /\d[.,]\d/.test(text)
      ? 'That looks like a diameter. Enter a 5-digit part number such as 99300, or use Measure my shaft.'
      : 'Enter a 5-digit part number, for example 99300.');
    this.#swap(this.refs.lookupResult, html);
    if (!r || r.kind === 'invalid') return;
    const key = `${r.kind}:${r.part?.pn || r.number || text}`;
    if (key !== this.#lastLookupKey) {
      this.#lastLookupKey = key;
      this.#track('lookup', { type: r.kind, found: Boolean(r.part) });
    }
    const heading = this.refs.lookupResult.querySelector('[data-sss-focus-heading]');
    if (heading) this.#announce(heading.textContent.replace(/\s+/g, ' ').trim(), announceNow);
  }

  #lookupHtml(r, text) {
    const number = r.number || this.core.normalizeQuery(text).digits || text;
    const brand = BRAND_LABEL[r.brand] || r.brand || '';
    const measureBtn = '<button type="button" class="button button--secondary sss-btn sss-btn--secondary" data-action="measure">Measure my shaft</button>';
    const head = (t) => `<h3 class="sss-card__heading" tabindex="-1" data-sss-focus-heading>${esc(t)}</h3>`;
    switch (r.kind) {
      case 'part':
        return this.#cardHtml(r.part, r.warning === 'same_number_competitor'
          ? '<p class="sss-note sss-note--info">If this number came from a Timken, National or FMS sleeve, it may be a different size (for example, Timken 99075 fits 0.748–0.752 in). Measure your shaft to be sure.</p>'
          : '');
      case 'legacy':
        return this.#cardHtml(r.part, `<p class="sss-note sss-note--info">${esc(r.from)} is an old number. The current SKF part is ${esc(r.part.pn)}.</p>`);
      case 'competitor':
        // r.note holds the internal verification evidence (overrides.csv); customers get a plain sentence.
        return this.#cardHtml(r.part, `<p class="sss-note sss-note--info">${esc(brand)} ${esc(number)} is the same size as SKF Speedi-Sleeve ${esc(r.part.pn)}: same shaft range and width on the shaft.</p>`);
      case 'competitor_unknown':
        return `<div class="sss-card">${head(`${brand} ${number}`)}
          <p>We haven't verified a match for this number. The same number can be a different size between brands, so measure your shaft, or send us the number and we'll match it.</p>
          <div class="sss-card__actions">${measureBtn}${this.#quoteButton('competitor_unknown', 'Send us the number', [], 'primary')}</div></div>`;
      case 'excluded':
        return `<div class="sss-card">${head(`SKF ${r.part?.pn || number}`)}
          <p>We need to confirm this part with SKF before we can recommend it. Ask us and we'll check.</p>
          <div class="sss-card__actions">${this.#quoteButton('excluded', 'Ask us to confirm', r.part ? [r.part.pn] : [], 'primary')}</div></div>`;
      case 'ldslv_maybe':
        return `<div class="sss-card">${head(`${number}: LDSLV heavy-duty sleeve`)}
          <p>This looks like an SKF LDSLV heavy-duty wear sleeve number. LDSLVs are made to order.</p>
          <div class="sss-rte">${this.#slotHtml('ldslv')}</div>
          <div class="sss-card__actions">${this.#quoteButton('ldslv', 'Request an LDSLV quote', [], 'primary')}</div></div>`;
      default: {
        const stocked = this.listings.state === 'ready' && /^\d{5}$/.test(number)
          ? this.core.pickListing(number, this.listings.index, this.config.dupTag) : null;
        const listed = stocked && stocked.status !== 'unlisted' && stocked.listing
          ? `<p class="sss-note sss-note--info">We list SKF ${esc(number)} in the store, but its sizes aren't in our SKF table. Measure your shaft or ask us. <a href="${esc(safePath(stocked.listing.u, '#'))}">View the listing</a></p>` : '';
        return `<div class="sss-card">${head(`We couldn't find ${number}`)}
          <p>It isn't in SKF's current Speedi-Sleeve list. Measure your shaft to find the right size, or send us the number.</p>
          ${listed}
          <div class="sss-card__actions">${measureBtn}${this.#quoteButton('lookup_unknown', 'Send us the number', [], 'primary')}</div></div>`;
      }
    }
  }

  #cardHtml(part, preface) {
    const unit = this.state.unit;
    const { entry, action, blocked } = this.#lookupAction(part);
    const notes = [
      part.single_source && !part.whitelisted ? '<p class="sss-part__note">Listed in only one SKF edition — check availability.</p>' : '',
      part.type === 'UNCONFIRMED' ? '<p class="sss-part__note">SKF sources disagree on whether this is Standard or Gold. We will confirm.</p>' : '',
    ].join('');
    return `
      <article class="sss-card" aria-label="SKF ${esc(part.pn)}">
        <h3 class="sss-card__heading" tabindex="-1" data-sss-focus-heading>SKF ${esc(part.pn)} <span class="sss-type sss-type--${esc(String(part.type).toLowerCase())}">${esc(TYPE_SHORT[part.type] || part.type)}</span></h3>
        ${preface}
        ${blocked.length && !this.design ? this.#blockedHtml(blocked, [part.pn], true) : ''}
        ${this.#specsHtml(part, unit, { listing: entry.listing.listing, twinButton: true })}
        ${notes}
        <div class="sss-card__buy sss-part__buy">${this.#buyHtml(entry, action, unit, 'l')}</div>
        <p class="sss-card__measure">Not sure it fits? <button type="button" class="sss-btn sss-btn--link" data-action="measure">Measure your shaft</button> to check.</p>
        ${this.#toolsHtml()}
      </article>
      ${this.#checklistHtml(null)}`;
  }

  /* ----- Add to cart (mirrors Stockmart / Dawn product-form.js; exactly one POST per click) ----- */

  #measuredProperty() {
    const vm = this.lastVm;
    if (this.state.mode === 'measure' && vm?.summary) {
      return `${this.#meanText(vm)} (avg of ${vm.summary.n})`;
    }
    return 'not measured (part number lookup)';
  }

  #qtyFor(pn, listing) {
    const [min = 1, step = 1, max = null] = Array.isArray(listing.q) ? listing.q : [];
    let v = parseInt(this.qty.get(pn) ?? min, 10);
    if (!Number.isFinite(v) || v < min) v = min;
    if (step > 1 && v % step) v = Math.ceil(v / step) * step;
    if (max && v > max) v = max;
    return v;
  }

  async #addToCart(btn, pn) {
    if (!pn || this.cartBusy.has(pn) || btn.disabled) return;
    const listing = this.#listingFor(pn).listing;
    if (!listing?.id) return;
    this.cartBusy.add(pn);
    this.cartMsg.delete(pn);
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    const label = btn.querySelector('[data-sss-label]');
    if (label) label.textContent = 'Adding…';

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    try { cart?.setActiveElement?.(btn); } catch { /* theme API differs */ }
    const body = new FormData();
    body.append('id', String(listing.id));
    body.append('quantity', String(this.#qtyFor(pn, listing)));
    body.append('properties[Shaft dia measured]', this.#measuredProperty());
    body.append('properties[_sss]', `${VERSION} ${this.data.edition_short || ''}`.trim());
    if (cart) {
      try {
        body.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
        body.append('sections_url', window.location.pathname);
      } catch { /* drawer without section rendering: the add still goes through */ }
    }

    let json;
    try {
      const res = await fetch(this.config.routes.cart_add_url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body,
        credentials: 'same-origin',
      });
      json = await res.json();
    } catch {
      this.#cartDone(pn, { kind: 'error', text: "Couldn't reach the cart. Check your connection, then open your cart to see if the item was added." }, btn);
      return;
    }

    if (!json || json.status) {
      const text = (json && (json.description || json.message)) || "This item couldn't be added to your cart.";
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try {
          // eslint-disable-next-line no-undef
          publish(PUB_SUB_EVENTS.cartError, { source: 'speedi-sleeve-selector', productVariantId: listing.id, errors: json?.errors || json?.description, message: json?.message });
        } catch { /* theme listener failed */ }
      }
      this.#cartDone(pn, { kind: 'error', text: String(text) }, btn);
      return;
    }

    this.#track('add_to_cart', { pn });
    if (!cart) {
      window.location.href = this.config.routes.cart_url;
      return;
    }
    cart.classList.remove('is-empty');
    let rendered = true;
    try { cart.renderContents(json); } catch { rendered = false; }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        // eslint-disable-next-line no-undef
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'speedi-sleeve-selector', productVariantId: listing.id, cartData: json });
      } catch { /* theme listener failed */ }
    }
    this.#cartDone(pn, rendered ? { kind: 'ok', text: 'Added to cart.' } : { kind: 'ok', text: 'Added —', link: true }, btn);
  }

  /** Update the clicked row in place so the drawer's setActiveElement(btn) target survives. */
  #cartDone(pn, message, btn) {
    this.cartBusy.delete(pn);
    this.cartMsg.set(pn, message);
    const buy = btn?.isConnected ? btn.closest('.sss-part__buy') : null;
    if (!buy) { this.#refresh(); return; }
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    const label = btn.querySelector('[data-sss-label]');
    if (label) label.textContent = 'Add to cart';
    buy.querySelector('.sss-buy__msg')?.remove();
    buy.insertAdjacentHTML('beforeend', this.#cartMsgHtml(pn));
    this.#html.delete(this.refs.results);
    this.#html.delete(this.refs.lookupResult);
  }

  #cartMsgHtml(pn) {
    const msg = this.cartMsg.get(pn);
    if (!msg) return '';
    const link = msg.link ? ` <a href="${esc(this.config.routes.cart_url)}">View cart</a>` : '';
    return `<p class="sss-buy__msg sss-buy__msg--${msg.kind}" role="status">${esc(msg.text)}${link}</p>`;
  }

  /* ----- Quote (server-rendered contact form) ----- */

  #wireQuoteForm() {
    this.quotePanel = this.querySelector('[data-sss-quote]');
    this.quoteForm = this.quotePanel?.querySelector('form') || null;
    if (!this.quoteForm) return;
    this.quoteForm.noValidate = true;
    const intro = this.quotePanel.querySelector('[data-sss-quote-intro]');
    if (intro && !intro.textContent.trim()) intro.innerHTML = this.#slotHtml('quote_intro');
    const success = this.quotePanel.querySelector('[data-sss-quote-success]');
    if (success && !success.textContent.trim()) success.innerHTML = this.#slotHtml('quote_success');
    if (!this.config?.quote?.hasErrors) this.#showLdslvField(false);
    if (!this.quoteForm.querySelector('[data-sss-quote-jserrors]')) {
      this.quoteForm.insertAdjacentHTML('afterbegin', '<div data-sss-quote-jserrors></div>');
    }
  }

  #openQuote(reason, pns = []) {
    if (!this.quotePanel || !this.quoteForm) return;
    this.quoteCtx = { reason: REASON_LABEL[reason] ? reason : 'general', pns: pns.filter((p) => PN_RE.test(p)) };
    this.#fillQuoteHidden();
    this.#renderQuoteContext();
    this.quotePanel.querySelector('[data-sss-quote-success]')?.setAttribute('hidden', '');
    this.quotePanel.hidden = false;
    const heading = this.quotePanel.querySelector('.sss-quote__heading');
    heading?.focus({ preventScroll: true });
    heading?.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
    this.#track('quote_open', { reason: this.quoteCtx.reason });
  }

  #renderQuoteContext() {
    const box = this.quotePanel?.querySelector('[data-sss-quote-context]');
    if (!box) return;
    const { reason, pns } = this.quoteCtx;
    const list = pns.length ? `SKF ${pns.join(', ')}` : '';
    const number = this.lastLookup?.number || this.lastLookup?.text || '';
    const text = {
      sold_out: `${list} is out of stock. We'll check the lead time.`,
      unlisted: `${list} isn't stocked online. We can order it for you.`,
      borderline: `SKF's published data doesn't give a clear fit for ${list} at your reading. We'll confirm the fit.`,
      confirm_fit: `We'll confirm ${list} fits your shaft.`,
      single_source: `We'll check ${list} is still available from SKF.`,
      ldslv: "We'll quote an SKF LDSLV heavy-duty sleeve.",
      gold_on_request: `We'll ask SKF about a Gold version${list ? ` of ${list}` : ''}.`,
      blocked: "A Speedi-Sleeve can't be fitted as described. We'll suggest alternatives.",
      geometry: "The matching sleeves don't fit your worn band position. We'll look at options.",
      lookup_unknown: `We'll match part number ${number}.`,
      competitor_unknown: `We'll match part number ${number}.`,
      excluded: `We'll confirm ${list || number} with SKF.`,
      general: '',
    }[reason] ?? "No standard Speedi-Sleeve fits your shaft. We'll look at options.";
    box.innerHTML = text
      ? `<p>${esc(text.replace(/\s+/g, ' ').trim())}</p><button type="button" class="sss-btn sss-btn--link" data-action="quote-back">Back to the size finder</button>`
      : '<button type="button" class="sss-btn sss-btn--link" data-action="quote-back">Back to the size finder</button>';
    box.hidden = false;
    this.#showLdslvField(reason === 'ldslv');
    if (reason === 'ldslv') this.quoteForm.querySelector('.sss-quote__more')?.setAttribute('open', '');
  }

  /** LDSLV option: LDSLV quotes only (always shown without JS). */
  #showLdslvField(show) {
    const field = this.quoteForm?.querySelector('[data-sss-ldslv-field]');
    if (!field) return;
    field.hidden = !show;
    const select = field.querySelector('select');
    if (select) select.disabled = !show;
  }

  #focusAfterQuote() {
    const target = this.state.mode === 'lookup'
      ? this.refs?.lookupResult.querySelector('[data-sss-focus-heading]') || this.refs?.lookupInput
      // No mode yet (a size chart "Quote" link): back to the entry tabs.
      : this.#resultsHeading() || this.querySelector(this.state.mode ? '[data-sss-reading="0"]' : '.sss-entry__btn');
    if (!target) return;
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  /** Typed values in one unit (share, quote): their own when all agree, else mm (in -> mm is exact). */
  #typed() {
    const s = this.state;
    const readings = s.readings.filter(filled).slice(0, MAX_READINGS);
    const geo = GEO_KEYS.filter((k) => filled(s.geo[k]));
    const units = new Set([...readings, ...geo.map((k) => s.geo[k])].map((v) => v.unit));
    const unit = units.size === 1 ? [...units][0] : units.size ? 'mm' : s.unit;
    const text = (v) => this.#shown(v, unit).trim();
    return { unit, readings: readings.map(text), geo: Object.fromEntries(geo.map((k) => [k, text(s.geo[k])])) };
  }

  /** #sss= code (no personal data); '' without a valid reading. */
  sssShareCode() {
    if (!this.core) return '';
    const t = this.#typed();
    const a = this.state.answers;
    try {
      return this.core.encodeShare({ unit: t.unit, readings: t.readings, ...t.geo, answers: { k: a.k, e: a.e, l: a.l, g: a.g === 'deep', a: a.a, f: a.f } });
    } catch { return ''; }
  }

  #quoteSummaryCtx() {
    const vm = this.lastVm;
    const { reason, pns } = this.quoteCtx;
    const measure = this.state.mode === 'measure';
    const matched = measure ? (vm?.groups || []).flatMap((g) => g.entries) : [];
    const stateOf = new Map([...matched, ...(measure ? vm?.match?.excludedHits || [] : [])].map((e) => [e.part.pn, e.state]));
    const explicit = pns.map((pn) => this.partsByPn?.get(pn)).filter(Boolean)
      .map((part) => ({ part, state: stateOf.get(part.pn) }));
    const typed = vm?.summary ? this.#typed() : null;
    return {
      reason,
      unit: typed?.unit || this.state.unit,
      readings: typed?.readings || [],
      summary: vm?.summary || null,
      candidates: explicit.length ? explicit : matched.map((e) => ({ part: e.part, state: e.state })),
      band: vm?.geo?.values || null,
      edition: this.data?.edition_short || '',
      shareCode: typed ? this.sssShareCode() : '',
      lookup: this.state.mode === 'lookup' ? this.lastLookup : null,
    };
  }

  /** Hidden quote fields ("none" when blank). */
  sssQuoteFields() {
    const ctx = this.#quoteSummaryCtx();
    let fields = {};
    try { fields = this.core?.buildQuoteSummary(ctx) || {}; } catch (error) { console.warn(`[speedi-sleeve-selector ${VERSION}] quote summary`, error); }
    const out = { Form: `Speedi-Sleeve quote – ${REASON_LABEL[ctx.reason] || 'general'}` };
    for (const key of QUOTE_KEYS) out[key] = cleanQuoteValue(fields[key]) || 'none';
    if (!fields.Reason) out.Reason = ctx.reason.replace(/_/g, ' ');
    if (!fields.Unit) out.Unit = ctx.unit;
    return out;
  }

  #fillQuoteHidden() {
    if (!this.quoteForm) return;
    for (const [key, value] of Object.entries(this.sssQuoteFields())) {
      const input = this.quoteForm.querySelector(`[data-sss-hidden="${key}"]`);
      if (input) input.value = value;
    }
  }

  #onQuoteSubmit(event) {
    const form = this.quoteForm;
    if (this.design) {
      event.preventDefault();
      this.#swapQuoteErrors([{ id: '', text: 'Quote requests are turned off in the theme editor.' }]);
      return;
    }
    if (this.quoteSubmitting) { event.preventDefault(); return; } // one email per double tap
    const name = form.querySelector('[name="contact[name]"]');
    const email = form.querySelector('[name="contact[email]"]');
    const errors = [];
    const setErr = (input, message) => {
      if (!input) return;
      const box = document.getElementById(`${input.id}-error`);
      if (message) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
      if (box) { box.textContent = message; box.hidden = !message; }
      if (message) errors.push({ id: input.id, text: message });
    };
    setErr(name, name && !name.value.trim() ? 'Enter your name' : '');
    const ev = email?.value.trim() || '';
    setErr(email, !ev ? 'Enter your email address' : (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ev) ? 'Enter an email address like name@example.com' : ''));
    if (errors.length) {
      event.preventDefault();
      this.#swapQuoteErrors(errors);
      return;
    }
    this.#swapQuoteErrors([]);
    this.#fillQuoteHidden();
    this.#saveRoundTrip();
    this.#track('quote_submit', { reason: this.quoteCtx.reason });
    this.quoteSubmitting = true;
    // Disable after this submit; re-enable if the page stays (captcha closed) or returns (bfcache).
    setTimeout(() => { const b = form.querySelector('[type="submit"]'); if (b) b.disabled = true; }, 0);
    clearTimeout(this.submitTimer);
    this.submitTimer = setTimeout(() => this.#submitDone(), 10000);
  }

  #submitDone() {
    clearTimeout(this.submitTimer);
    this.quoteSubmitting = false;
    const b = this.quoteForm?.querySelector('[type="submit"]');
    if (b) b.disabled = false;
  }

  #swapQuoteErrors(errors) {
    const box = this.quoteForm?.querySelector('[data-sss-quote-jserrors]');
    if (!box) return;
    box.innerHTML = errors.length
      ? this.#errorSummaryHtml('Check the form', errors, 4).replace(/<a href="#">([^<]*)<\/a>/g, '$1')
      : '';
    box.querySelector('.sss-errors')?.focus();
  }

  #saveRoundTrip() {
    const fields = {};
    for (const el of this.quoteForm.querySelectorAll(KEEP_FIELDS)) fields[el.name] = el.value;
    session.set('sss:state', JSON.stringify({
      v: 2, t: Date.now(),
      mode: this.state.mode, unit: this.state.unit, readings: this.state.readings, geo: this.state.geo,
      answers: this.state.answers, lookupText: this.state.lookupText, quote: this.quoteCtx, fields,
    }));
  }

  /** After the contact post: restore the tool (and the form on errors). */
  #restoreRoundTrip() {
    const q = this.config.quote;
    const raw = session.get('sss:state');
    if (raw) session.remove('sss:state');
    if (!q.posted && !q.hasErrors) return false;
    const saved = readJson(raw);
    if (saved && (saved.v === 1 || saved.v === 2) && Date.now() - Number(saved.t) < ROUND_TRIP_TTL_MS) {
      // Validate everything: any script on the page can write this storage
      const st = this.state;
      st.unit = saved.unit === 'mm' ? 'mm' : 'in';
      const typed = (v) => (v && typeof v === 'object' ? src(v.text, v.unit) : src(v, st.unit)); // v1: text
      if (Array.isArray(saved.readings)) st.readings = saved.readings.slice(0, MAX_READINGS).map(typed);
      for (const k of GEO_KEYS) if (saved.geo?.[k] != null) st.geo[k] = typed(saved.geo[k]);
      const a = saved.answers || {};
      st.answers = { k: a.k === true, e: a.e === true, l: a.l === true, g: ['light', 'deep'].includes(a.g) ? a.g : null, a: a.a === true, f: a.f === true };
      st.lookupText = String(saved.lookupText || '').slice(0, 100);
      const sq = saved.quote || {};
      this.quoteCtx = {
        reason: REASON_LABEL[sq.reason] ? sq.reason : 'general',
        pns: Array.isArray(sq.pns) ? sq.pns.filter((p) => PN_RE.test(p)).slice(0, 40) : [],
      };
      this.#syncInputs();
      this.#touchFilled();
      if (saved.mode === 'measure' || saved.mode === 'lookup') this.#setMode(saved.mode);
      if (q.hasErrors && saved.fields) {
        for (const el of this.quoteForm?.querySelectorAll(KEEP_FIELDS) || []) {
          if (!el.value && typeof saved.fields[el.name] === 'string') el.value = saved.fields[el.name].slice(0, 2000);
        }
      }
      if (q.hasErrors) { this.#fillQuoteHidden(); this.#renderQuoteContext(); }
    }
    if (this.quotePanel) this.quotePanel.hidden = false;
    const focusTarget = this.quotePanel?.querySelector(q.hasErrors ? '[data-sss-quote-errors]' : '[data-sss-quote-success]');
    focusTarget?.focus();
    return true;
  }

  /** Restored values show their errors at once. */
  #touchFilled() {
    this.state.readings.forEach((v, i) => { if (filled(v)) this.touched.add(`r${i}`); });
    for (const k of GEO_KEYS) if (filled(this.state.geo[k])) this.touched.add(k);
  }

  /* ----- Share link, sample, print ----- */

  #applyHash() {
    const hash = window.location.hash;
    if (!hash.startsWith('#sss=')) return false;
    let decoded = null;
    try { decoded = this.core.decodeShare(hash); } catch { decoded = null; }
    if (!decoded || !Array.isArray(decoded.readings)) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
      return false;
    }
    const st = this.state;
    st.unit = decoded.unit === 'mm' ? 'mm' : 'in';
    st.readings = decoded.readings.slice(0, MAX_READINGS).map((t) => src(t, st.unit));
    for (const k of GEO_KEYS) st.geo[k] = src(decoded[k] ?? '', st.unit);
    const a = decoded.answers || {};
    st.answers = { k: Boolean(a.k), e: Boolean(a.e), l: Boolean(a.l), g: a.g ? 'deep' : null, a: Boolean(a.a), f: Boolean(a.f) };
    this.#syncInputs();
    this.#touchFilled();
    const hasCheck = GEO_KEYS.some((k) => filled(st.geo[k])) || ['k', 'e', 'l', 'a', 'f'].some((k) => st.answers[k]) || st.answers.g;
    if (hasCheck && this.refs.check) { this.refs.check.open = true; this.openDetails.add('check'); }
    this.#setMode('measure');
    this.#update({ announceNow: true });
    this.#focusResults();
    return true;
  }

  #applySample() {
    this.state.readings = [src('1.0000'), src(), src()];
    this.state.unit = 'in';
    this.#syncInputs();
    this.#setMode('measure');
  }

  #shareText(vm) {
    const lines = [];
    if (vm?.summary) {
      const readings = this.state.readings.filter(filled).map((v) => this.#shown(v).trim()).join(', ');
      lines.push(`SKF Speedi-Sleeve for a ${this.#meanText(vm)} shaft (average of ${vm.summary.n} ${plural(vm.summary.n, 'reading', 'readings')}: ${readings} ${vm.unit}).`);
    }
    const entries = (vm?.groups || []).flatMap((g) => g.entries);
    if (entries.length) {
      lines.push('Matching parts:');
      for (const e of entries.slice(0, 10)) {
        const li = e.listing?.listing;
        const buy = e.action?.action === 'cart' ? `${this.config.showPrices && li?.pf ? `${li.pf}, ` : ''}available`
          : e.action?.action === 'quote' ? 'ask for a quote' : e.action?.reason === 'spread' ? 're-measure first' : 'not orderable as measured';
        lines.push(`- SKF ${e.part.pn} ${TYPE_SHORT[e.part.type] || ''}, covers ${this.#dimText(e.part, 'b1', vm.unit)}, ${buy}`);
      }
    } else if (vm?.match) {
      lines.push(this.#announceText(vm));
    }
    lines.push(`Based on SKF published data${this.data?.edition_short ? ` (${this.data.edition_short})` : ''}. Check the fit before ordering.`);
    return lines.join('\n');
  }

  async #share() {
    const vm = this.lastVm;
    const code = this.sssShareCode();
    const url = `${window.location.origin}${window.location.pathname}${code ? `#sss=${code}` : ''}`;
    const text = this.#shareText(vm);
    if (navigator.share) {
      try {
        await navigator.share({ title: 'SKF Speedi-Sleeve size', text, url });
        this.#track('share', { method: 'share' });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      this.toolsMsg = { html: 'Copied. Paste it into an email or message to your purchasing team.' };
      this.#track('share', { method: 'copy' });
    } catch {
      const id = `${this.idBase}-share-text`;
      this.toolsMsg = { html: `<label class="sss-field__label" for="${id}">Copy this and send it to your purchasing team:</label><textarea class="sss-field__input sss-field__input--area" id="${id}" rows="6" readonly>${esc(`${text}\n${url}`)}</textarea>` };
      this.#track('share', { method: 'manual' });
    }
    this.#refresh();
    const ta = this.querySelector(`#${this.idBase}-share-text`);
    if (ta) { ta.focus(); ta.select(); }
  }

  #print() {
    this.refs.print.innerHTML = this.#printSheetHtml();
    document.documentElement.classList.add('sss-printing');
    this.#track('print', { mode: this.state.mode || 'measure' });
    window.print();
    if (!('onafterprint' in window)) setTimeout(() => document.documentElement.classList.remove('sss-printing'), 1000);
  }

  #printSheetHtml() {
    const unit = this.state.unit;
    const date = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    const rowsFor = (entries) => entries.slice(0, 12).map((e) => {
      const p = e.part;
      const g = p.geom || {};
      const val = (k) => (g[`${k}_${unit}`] != null ? fixed(g[`${k}_${unit}`], unit) : '–');
      const range = unit === 'in' ? p.range?.display_in : p.range?.display_mm;
      const st = FIT_SHORT[e.state] || '';
      const li = e.listing?.listing;
      const buy = e.action?.action === 'cart' ? (this.config.showPrices && li?.pf ? li.pf : 'Available')
        : e.action?.action === 'quote' ? 'Quote' : e.action?.reason === 'spread' ? 'Re-measure' : '';
      return `<tr><td>${esc(p.pn)}</td><td>${esc(p.type === 'GOLD' ? 'Gold' : p.type === 'STD' ? 'STD' : 'TBC')}</td><td>${Array.isArray(range) ? `${fixed(range[0], unit)}–${fixed(range[1], unit)}` : ''}</td><td>${val('b1')}</td><td>${val('b')}</td><td>${val('D')}</td><td>${val('B')}</td><td>${esc(st)}</td><td>${esc(buy)}</td></tr>`;
    }).join('');
    let intro = '';
    let entries = [];
    const vm = this.lastVm;
    if (this.state.mode === 'lookup') {
      const part = this.lastLookup?.part;
      if (part) {
        const { entry, action } = this.#lookupAction(part);
        entries = [{ ...entry, state: null, action }];
        intro = `<p>Part number lookup: specs only, no shaft readings.${this.lastLookup.from ? ` ${esc(this.lastLookup.from)} is replaced by ${esc(part.pn)}.` : ''}</p>`;
      }
    } else if (vm?.summary) {
      const readings = this.state.readings.filter(filled).map((v) => this.#shown(v).trim()).join(', ');
      intro = `<p>Shaft readings: ${esc(readings)} ${unit}. Average ${esc(this.#meanText(vm))}${vm.summary.n > 1 ? `, spread ${esc(this.#len(vm.summary.spread_mm, unit))}` : ''}.</p>`;
      if (vm.summary.nominal) intro += '<p><strong>Possible sizes only: measure to 0.001 in (0.01 mm) to confirm.</strong></p>';
      if (vm.match && vm.match.zone !== 'speedi') intro += `<p><strong>${esc(this.#announceText(vm))}</strong></p>`;
      entries = vm.groups.flatMap((g) => g.entries);
    }
    const placements = entries.filter((e) => e.geometry && (e.geometry.status === 'ok' || e.geometry.status === 'pipe'))
      .map((e) => `<li>SKF ${esc(e.part.pn)}: push until the flange-side edge is ${esc(this.#len(e.geometry.place_mm, unit, unit === 'in' ? 3 : 2))} from the shaft end (outer edge at ${esc(this.#len(e.geometry.outer_edge_mm, unit, unit === 'in' ? 3 : 2))})${e.geometry.status === 'pipe' ? '; use a pipe, the included tool is too short' : ''}.</li>`).join('');
    const band = vm?.geo?.input
      ? `<p>Worn band: ${esc(this.#len(vm.geo.input.tn, unit))} to ${esc(this.#len(vm.geo.input.tf, unit))} from the shaft end.</p>` : '';
    const disclaimer = this.#slotHtml('disclaimer');
    return `
      <div class="sss-print__sheet">
        <h2 class="sss-print__title">SKF Speedi-Sleeve spec sheet</h2>
        <p class="sss-print__meta">Printed ${esc(date)} from ${esc(window.location.hostname)}. Dimensions in ${unit === 'in' ? 'inches' : 'millimetres'}.</p>
        ${intro}
        ${entries.length ? `<table class="sss-print__table"><thead><tr><th>Part</th><th>Type</th><th>d1</th><th>b1</th><th>b</th><th>D</th><th>B</th><th>Fit</th><th>Buy</th></tr></thead><tbody>${rowsFor(entries)}</tbody></table>` : ''}
        ${band}
        ${placements ? `<p><strong>Placement</strong></p><ul>${placements}</ul>` : ''}
        ${this.#checkOutHtml(vm || { geo: {}, groups: [], answers: this.state.answers }, { print: true })}
        <p><strong>Installation checklist</strong></p>
        <ol class="sss-print__steps">${this.#checklistSteps(vm).map((s) => `<li>${s}</li>`).join('')}</ol>
        <p class="sss-print__legend">${LEGEND.map(([k, v]) => `<span><strong>${esc(k)}</strong> = ${esc(v)}</span>`).join('; ')}.</p>
        <div class="sss-print__small">${disclaimer}
          <p>Based on SKF published data${this.data?.edition ? `: ${esc(this.data.edition)}` : ''}. SKF&reg; and Speedi-Sleeve&reg; are registered trademarks of the SKF Group. Verify measurements; installed sleeves cannot be reused.</p>
        </div>
      </div>`;
  }

  /* ----- Analytics (no personal data) ----- */

  #track(event, payload = {}) {
    if (this.design) return;
    try { window.Shopify?.analytics?.publish?.(`apollo_sss:${event}`, payload); } catch { /* analytics unavailable */ }
    try { window.dataLayer?.push?.({ event: `apollo_sss_${event}`, ...payload }); } catch { /* no GTM */ }
  }

  #trackSearch(vm) {
    if (this.state?.mode !== 'measure' || !vm?.summary || vm.hold || !vm.match) return;
    const meanIn = Math.round(this.core.fromMm(vm.summary.mean_mm, 'in') * 1000) / 1000;
    const key = `${meanIn}:${vm.unit}:${vm.match.zone}:${vm.count}`;
    if (key === this.#lastSearchKey) return;
    this.#lastSearchKey = key;
    if (vm.match.zone === 'speedi') this.#track('search', { mean_in: meanIn, unit: vm.unit, count: vm.count });
    else this.#track('no_match', { diameter_in: meanIn, zone: vm.match.zone });
  }

  /* ----- Load error ----- */

  #renderLoadError() {
    if (!this.#listenersBound) this.#bindLocal();
    if (!this.quoteForm) this.#wireQuoteForm();
    const coll = safePath(this.config?.collectionUrl, '/collections/skf-speedi-sleeves');
    this.#status(`
      <div class="sss-note sss-note--block sss-loaderror" role="alert">
        <p><strong>The size finder didn't load.</strong> Check your connection and try again.</p>
        <p><button type="button" class="button button--secondary sss-btn sss-btn--secondary" data-action="retry">Try again</button></p>
        <p>You can also <a href="${esc(coll)}">browse all SKF Speedi-Sleeves</a>${this.quoteForm ? ' or <button type="button" class="sss-btn sss-btn--link" data-action="quote" data-reason="general">send us your readings</button>' : ''}.</p>
      </div>`, true);
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('speedi-sleeve-selector')) {
  customElements.define('speedi-sleeve-selector', SpeediSleeveSelector);
}
